import { useState, useEffect, useRef } from 'react';
import { 
  LayoutDashboard, Receipt, Wallet, CreditCard, History, UserCircle, 
  MessageSquare, Gift, Settings, ChevronDown, Search, Phone, Plus,
  X, Check, Copy, RefreshCw, LogOut, Bell, User, Menu, Clock, ExternalLink, Server, Key, Eye, EyeOff, Mail, Camera, Shield, Plane
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { AirtimeSection, BillsPaymentSection } from '../components/BillPaymentSections';
import { VirtualNumbersSection } from '../components/VirtualNumbersSection';
import { FundWalletSection } from '../components/FundWalletSection';
import { VirtualCardsSection } from '../components/VirtualCardsSection';
import { TravelSection } from '../components/TravelSection';
import AutoLogout from '../components/AutoLogout';

const API = process.env.REACT_APP_BACKEND_URL;

// Shared Select styles to prevent blurry text and ensure dark, visible text
const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '48px',
    borderWidth: '2px',
    borderColor: 'hsl(var(--input))',
    '&:hover': { borderColor: 'hsl(var(--primary))' }
  }),
  placeholder: (base) => ({
    ...base,
    color: 'hsl(var(--muted-foreground))'
  }),
  singleValue: (base) => ({
    ...base,
    color: 'hsl(var(--foreground))',
    fontWeight: '500'
  }),
  input: (base) => ({
    ...base,
    color: 'hsl(var(--foreground))'
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? '#eff6ff' : state.isSelected ? '#dbeafe' : 'white',
    color: 'hsl(var(--foreground))',
    cursor: 'pointer',
    fontWeight: state.isSelected ? '600' : '400'
  })
};

const NewDashboard = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('dashboard'); // Default to Dashboard
  const [defaultBillTab, setDefaultBillTab] = useState(null); // For pre-selecting bill payment tab
  const [sidebarOpen, setSidebarOpen] = useState(false); // Default closed on mobile
  const [user, setUser] = useState({ email: '', full_name: '', ngn_balance: 0, usd_balance: 0, is_admin: false });
  const [loading, setLoading] = useState(true); // Start with loading true
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [pageToggles, setPageToggles] = useState({
    enable_dashboard: true,
    enable_transactions: true,
    enable_fund_wallet: true,
    enable_virtual_numbers: true,
    enable_buy_data: true,
    enable_airtime: true,
    enable_betting: true,
    enable_virtual_cards: true,
    enable_sms_history: true,
    enable_account_upgrade: true,
    enable_referral: true,
    enable_profile: true,
    enable_support: true,
    // Payment gateway toggles
    enable_paymentpoint: true,
    enable_plisio: true,
    enable_ercaspay: true,
    enable_transactpay: true
  });
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loginPopups, setLoginPopups] = useState([]);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [currentPopupIndex, setCurrentPopupIndex] = useState(0);
  
  // Dark mode state
  const [darkMode, setDarkMode] = useState(false);
  
  // Virtual Numbers state
  const [selectedServer, setSelectedServer] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [areaCode, setAreaCode] = useState('');
  const [availableServices, setAvailableServices] = useState([]);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const [paymentCurrency, setPaymentCurrency] = useState('USD');
  const [purchaseExpanded, setPurchaseExpanded] = useState(true);
  const [dashboardCurrency, setDashboardCurrency] = useState('NGN'); // For dashboard balance toggle
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const [selectedAreaCodes, setSelectedAreaCodes] = useState([]);
  const [preferredNumber, setPreferredNumber] = useState('');
  const [purchasing, setPurchasing] = useState(false);
  
  // Crypto funding state
  const [cryptoAmountUsd, setCryptoAmountUsd] = useState('');
  const [cryptoCurrency, setCryptoCurrency] = useState('USDT');
  const [currentDeposit, setCurrentDeposit] = useState(null);
  const [creatingDeposit, setCreatingDeposit] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [cryptoCountdown, setCryptoCountdown] = useState(null);
  const [cryptoNetwork, setCryptoNetwork] = useState('TRON');

  // Get cached branding from localStorage to prevent color flash
  const getCachedBranding = () => {
    try {
      const cached = localStorage.getItem('app_branding_cache');
      if (cached) return JSON.parse(cached);
    } catch (e) {}
    return null;
  };

  const cachedBranding = getCachedBranding();

  // Public branding (used for brand text + primary green accents)
  const [branding, setBranding] = useState(cachedBranding || {
    brand_name: 'Social SMS WRLD',
    brand_logo_url: 'https://cloudsmsservice.org/img/social_logo.png',
    primary_color_hex: '#059669',
    secondary_color_hex: 'hsl(var(--primary))',
    accent_color_hex: '#7c3aed',
    button_color_hex: '#7c3aed',
    header_bg_color_hex: '#ffffff',
    hero_gradient_from: 'hsl(var(--primary))',
    hero_gradient_to: '#06b6d4',
    landing_hero_title: 'Cheapest and Fastest\nOnline SMS Verification',
    landing_hero_subtitle:
      'Buy Premium Quality OTP in Cheapest Price and stay safe from unwanted promotional sms and calls and also prevent your identity from fraudsters',
    banner_images: []
  });

  const fetchBranding = async () => {
    try {
      const resp = await axios.get(`${API}/api/public/branding`);
      setBranding(resp.data);
      // Cache for next page load (prevents flash)
      localStorage.setItem('app_branding_cache', JSON.stringify(resp.data));
      // Set support channel URLs
      if (resp.data) {
        setSupportUrls({
          whatsapp_support_url: resp.data.whatsapp_support_url || 'https://wa.me/2348000000000',
          telegram_support_url: resp.data.telegram_support_url || 'https://t.me/yoursupport',
          support_email: resp.data.support_email || 'support@smsrelay.com'
        });
        // Update favicon if set
        if (resp.data.favicon_url) {
          updateFavicon(resp.data.favicon_url);
        }
      }
    } catch (e) {
      // ignore
    }
  };

  // Helper to update favicon in the document
  const updateFavicon = (url) => {
    let link = document.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = url;
  };

  // Orders
  const [orders, setOrders] = useState([]);
  const [transactions, setTransactions] = useState([]);
  
  // Reseller state (moved to parent to persist across section changes)
  const [resellerProfile, setResellerProfile] = useState(null);
  const [resellerPlans, setResellerPlans] = useState([]);
  const [resellerOrders, setResellerOrders] = useState([]);
  const [resellerLoading, setResellerLoading] = useState(true);
  const [resellerFetched, setResellerFetched] = useState(false);
  const [showResellerDocs, setShowResellerDocs] = useState(false); // Persist docs view
  const [resellerApiBaseUrl, setResellerApiBaseUrl] = useState(''); // Set from admin branding config
  
  // Support channel URLs (from admin branding config)
  const [supportUrls, setSupportUrls] = useState({
    whatsapp_support_url: 'https://wa.me/2348000000000',
    telegram_support_url: 'https://t.me/yoursupport',
    support_email: 'support@smsrelay.com'
  });
  
  // Travel feature visibility (site-specific - only shown on sites that enable it)
  const [showTravelFeature, setShowTravelFeature] = useState(false);
  
  const token = localStorage.getItem('token');
  const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };

  // Handle Ercaspay callback
  useEffect(() => {
    const handleErcaspayCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const fullUrl = window.location.href;
      
      // Check if this is an Ercaspay callback (handle malformed URL with double ?)
      if (fullUrl.includes('ercaspay_callback=true') || urlParams.get('ercaspay_callback')) {
        // Parse the reference from URL (handle both normal and malformed URLs)
        let paymentRef = urlParams.get('ref');
        let status = urlParams.get('status');
        
        // If URL is malformed (has ?ref=XXX?reference=XXX), extract from the full URL
        if (!status && fullUrl.includes('status=')) {
          const statusMatch = fullUrl.match(/status=([^&]+)/);
          status = statusMatch ? statusMatch[1] : null;
        }
        
        // Also try to get ref from malformed URL
        if (!paymentRef && fullUrl.includes('ref=')) {
          const refMatch = fullUrl.match(/ref=([^?&]+)/);
          paymentRef = refMatch ? refMatch[1] : null;
        }
        
        // Clean up the URL immediately
        window.history.replaceState({}, document.title, '/dashboard');
        
        if (paymentRef) {
          // Verify payment status with backend
          try {
            const verifyResp = await axios.get(`${API}/api/ercaspay/verify/${paymentRef}`, axiosConfig);
            const paymentStatus = verifyResp.data?.payment?.status;
            
            if (paymentStatus === 'paid') {
              toast.success('Payment successful! Your wallet has been credited.');
            } else if (paymentStatus === 'failed') {
              toast.error('Payment failed. Please try again.');
            } else if (paymentStatus === 'pending') {
              toast.info('Payment is being processed. Your wallet will be credited shortly.');
            } else {
              toast.info(`Payment status: ${paymentStatus || status || 'Processing'}`);
            }
          } catch (err) {
            // Fallback to URL status if verify fails
            if (status === 'PAID' || status === 'paid' || status === 'successful') {
              toast.success('Payment successful! Your wallet will be credited shortly.');
            } else if (status === 'FAILED' || status === 'failed') {
              toast.error('Payment failed. Please try again.');
            } else {
              toast.info('Payment processing. Please check your balance.');
            }
          }
          
          // Refresh data
          setTimeout(() => {
            fetchProfile();
            fetchTransactions();
          }, 1500);
        } else if (status) {
          // No ref but have status
          if (status === 'PAID' || status === 'paid' || status === 'successful') {
            toast.success('Payment successful! Your wallet will be credited shortly.');
          } else if (status === 'FAILED' || status === 'failed') {
            toast.error('Payment failed. Please try again.');
          } else {
            toast.info(`Payment status: ${status}`);
          }
          setTimeout(() => {
            fetchProfile();
            fetchTransactions();
          }, 1500);
        }
      }
    };
    
    if (token) {
      handleErcaspayCallback();
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchProfile(),
          fetchOrders(),
          fetchTransactions(),
          fetchPageToggles(),
          fetchBranding(),
          fetchNotifications(),
          fetchLoginPopups(),
          fetchTravelStatus()  // Check if travel feature should be visible
        ]);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
    
    // Load dark mode preference
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(savedDarkMode);
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
    
    // Removed setInterval polling to prevent component re-renders
    // that were causing input values (Ercaspay, promo codes) to reset.
    // Users can manually refresh data using the refresh buttons.
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/api/notifications`, axiosConfig);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  };

  const fetchLoginPopups = async () => {
    try {
      const response = await axios.get(`${API}/api/notifications/login-popups`, axiosConfig);
      const popups = response.data.popups || [];
      if (popups.length > 0) {
        setLoginPopups(popups);
        setShowLoginPopup(true);
      }
    } catch (error) {
      console.error('Failed to fetch login popups');
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.post(`${API}/api/notifications/${notificationId}/read`, {}, axiosConfig);
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const dismissNotification = async (notificationId) => {
    try {
      await axios.post(`${API}/api/notifications/${notificationId}/dismiss`, {}, axiosConfig);
      fetchNotifications();
    } catch (error) {
      console.error('Failed to dismiss notification');
    }
  };

  const dismissLoginPopup = async () => {
    if (loginPopups[currentPopupIndex]) {
      await dismissNotification(loginPopups[currentPopupIndex].id);
    }
    if (currentPopupIndex < loginPopups.length - 1) {
      setCurrentPopupIndex(currentPopupIndex + 1);
    } else {
      setShowLoginPopup(false);
      setCurrentPopupIndex(0);
    }
  };

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', newMode.toString());
    if (newMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const fetchPageToggles = async () => {
    try {
      const response = await axios.get(`${API}/api/user/page-toggles`, axiosConfig);
      setPageToggles(response.data);
    } catch (error) {
      console.error('Failed to fetch page toggles');
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/api/user/profile`, axiosConfig);
      setUser(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.replace('/');
      }
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/api/orders/list`, axiosConfig);
      setOrders(response.data.orders);
    } catch (error) {
      console.error('Failed to fetch orders');
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/api/transactions/list`, axiosConfig);
      setTransactions(response.data.transactions);
    } catch (error) {
      console.error('Failed to fetch transactions');
    }
  };

  // Fetch travel feature visibility (site-specific)
  const fetchTravelStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/travel/status`, axiosConfig);
      setShowTravelFeature(response.data.visible === true);
    } catch (error) {
      console.error('Failed to fetch travel status');
      setShowTravelFeature(false);
    }
  };

  // Fetch reseller data (called once when navigating to reseller section)
  const fetchResellerData = async () => {
    if (resellerFetched) return; // Already fetched, skip
    
    setResellerLoading(true);
    try {
      // Fetch API base URL from public branding
      try {
        const brandingRes = await axios.get(`${API}/api/public/branding`);
        if (brandingRes.data.reseller_api_base_url) {
          setResellerApiBaseUrl(brandingRes.data.reseller_api_base_url);
        }
      } catch (e) {
        console.error('Failed to fetch branding for API URL');
      }
      
      // Fetch profile
      try {
        const profileRes = await axios.get(`${API}/api/reseller/profile`, axiosConfig);
        setResellerProfile(profileRes.data);
      } catch (e) {
        if (e.response?.status !== 404) console.error('Failed to fetch reseller profile');
      }
      
      // Fetch plans
      try {
        const plansRes = await axios.get(`${API}/api/reseller/plans`, axiosConfig);
        setResellerPlans(plansRes.data.plans || []);
      } catch (e) {
        console.error('Failed to fetch reseller plans');
      }
      
      // Fetch orders if reseller
      if (resellerProfile?.is_reseller) {
        try {
          const ordersRes = await axios.get(`${API}/api/reseller/orders`, axiosConfig);
          setResellerOrders(ordersRes.data.orders || []);
        } catch (e) {
          console.error('Failed to fetch reseller orders');
        }
      }
      
      setResellerFetched(true);
    } catch (error) {
      console.error('Error fetching reseller data:', error);
    } finally {
      setResellerLoading(false);
    }
  };

  const handleLogout = () => {
    // Set logging out state to prevent errors during unmount
    setIsLoggingOut(true);
    // Clear all auth data immediately
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('branding');
    // Use replace to prevent back button issues
    window.location.replace('/');
  };

  // If logging out, show nothing to prevent errors
  if (isLoggingOut) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Crypto funding functions
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard!');
    } catch (err) {
      console.error('Clipboard write failed', err);
      toast.error('Unable to copy to clipboard in this browser.');
    }
  };

  const handleCreateCryptoInvoice = async () => {
    if (!cryptoAmountUsd || parseFloat(cryptoAmountUsd) < 1) {
      toast.error('Please enter a valid amount (minimum $1)');
      return;
    }

    const amt = parseFloat(cryptoAmountUsd);
    if (Number.isNaN(amt) || amt < 5.1) {
      toast.error('Minimum crypto deposit is $5.10');
      return;
    }

    setCreatingDeposit(true);
    try {
      const response = await axios.post(
        `${API}/api/crypto/plisio/create-invoice`,
        {
          amount_usd: amt,
          currency: cryptoCurrency,
          network: cryptoCurrency === 'USDT' ? cryptoNetwork : null,
        },
        axiosConfig
      );

      if (response.data.success) {
        setCurrentDeposit(response.data.deposit);
        if (response.data.deposit.expires_at) {
          const exp = new Date(response.data.deposit.expires_at).getTime();
          setCryptoCountdown(Math.max(0, Math.floor((exp - Date.now()) / 1000)));
        }
        toast.success('Crypto deposit created! Send payment to the address shown.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create crypto deposit');
    } finally {
      setCreatingDeposit(false);
    }
  };

  const handleRefreshCryptoStatus = async () => {
    if (!currentDeposit) return;

    setCheckingStatus(true);
    try {
      const response = await axios.get(
        `${API}/api/crypto/plisio/status/${currentDeposit.id}`,
        axiosConfig
      );

      if (response.data.success) {
        setCurrentDeposit(response.data.deposit);
        if (response.data.deposit.expires_at) {
          const exp = new Date(response.data.deposit.expires_at).getTime();
          setCryptoCountdown(Math.max(0, Math.floor((exp - Date.now()) / 1000)));
        }
        if (response.data.deposit.status === 'paid') {
          toast.success('Payment confirmed! Your wallet has been credited.');
          fetchProfile(); // Refresh balance
        }
      }
    } catch (error) {
      toast.error('Failed to check payment status');
    } finally {
      setCheckingStatus(false);
    }
  };

  // Load existing active crypto deposit on mount
  useEffect(() => {
    const loadCurrentCryptoDeposit = async () => {
      if (!token) return;
      try {
        const resp = await axios.get(`${API}/api/crypto/plisio/current`, axiosConfig);
        if (resp.data?.success && resp.data.deposit) {
          setCurrentDeposit(resp.data.deposit);
          if (resp.data.deposit.expires_at) {
            const exp = new Date(resp.data.deposit.expires_at).getTime();
            setCryptoCountdown(Math.max(0, Math.floor((exp - Date.now()) / 1000)));
          }
        }
      } catch (e) {
        // ignore
      }
    };

    loadCurrentCryptoDeposit();
  }, [token]);

  // Countdown timer for crypto deposit
  useEffect(() => {
    if (!currentDeposit || !currentDeposit.expires_at) {
      setCryptoCountdown(null);
      return;
    }
    const expTs = new Date(currentDeposit.expires_at).getTime();
    const tick = () => {
      const diff = Math.floor((expTs - Date.now()) / 1000);
      if (diff <= 0) {
        setCryptoCountdown(0);
        return;
      }
      setCryptoCountdown(diff);
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [currentDeposit]);

  useEffect(() => {
    const calculatePrice = async () => {
      if (!selectedServer || !selectedService) {
        setEstimatedPrice(null);
        return;
      }

      // For DaisySMS (US server), calculate price in NGN with advanced options
      if (selectedServer.value === 'us_server' && selectedService.price_ngn) {
        let baseNGN = selectedService.price_ngn;
        let additionalCost = 0;
        let breakdown = [`Base: ₦${baseNGN.toFixed(2)}`];
        
        // Add 35% for each advanced option selected
        if (selectedCarrier) {
          additionalCost += baseNGN * 0.35;
          breakdown.push(`Carrier (${selectedCarrier.label}): +₦${(baseNGN * 0.35).toFixed(2)}`);
        }
        if (selectedAreaCodes && selectedAreaCodes.length > 0) {
          additionalCost += baseNGN * 0.35;
          const codes = selectedAreaCodes.map(c => c.value).join(', ');
          breakdown.push(`Area Code (${codes}): +₦${(baseNGN * 0.35).toFixed(2)}`);
        }
        if (preferredNumber) {
          additionalCost += baseNGN * 0.35;
          breakdown.push(`Preferred Number: +₦${(baseNGN * 0.35).toFixed(2)}`);
        }
        
        const totalNGN = baseNGN + additionalCost;
        const totalUSD = totalNGN / 1500;
        
        setEstimatedPrice({
          final_usd: totalUSD,
          final_ngn: totalNGN,
          breakdown: breakdown
        });
      } else if (selectedCountry) {
        // Old calculation for other servers
        try {
          const response = await axios.post(
            `${API}/api/orders/calculate-price`,
            {
              server: selectedServer.value,
              service: selectedService.value,
              country: selectedCountry?.value
            },
            axiosConfig
          );
          
          if (response.data.success) {
            setEstimatedPrice(response.data);
          }
        } catch (error) {


          console.error('Failed to calculate price:', error);
        }
      }
    };

    calculatePrice();
  }, [selectedServer, selectedService, selectedCountry, selectedCarrier, selectedAreaCodes, preferredNumber]);

  const fetchServicesForServer = async (serverValue) => {
    if (!serverValue) {
      setAvailableServices([]);
      setAvailableCountries([]);
      return;
    }

    setServicesLoading(true);
    try {
      // Use aliased server endpoints (no provider names exposed)
      const serverEndpoints = {
        'us_server': 'us_server',
        'server1': 'server1',
        'server2': 'server3'  // TigerSMS
      };
      
      const endpoint = serverEndpoints[serverValue];
      
      if (serverValue === 'us_server') {
        // US Server - US only, fetch services directly
        const response = await axios.get(`${API}/api/services/${endpoint}`, axiosConfig);
        if (response.data.success) {
          const services = (response.data.services || []).map(service => ({
            value: service.value,
            label: service.name,
            name: service.name,
            price_usd: service.final_price,
            price_ngn: service.final_price * 1500,
            count: service.count
          }));
          setAvailableServices(services);
          setAvailableCountries([{ value: '187', label: 'United States' }]);
        }
      } else if (serverValue === 'server1') {
        // Server 1 - fetch countries first
        const response = await axios.get(`${API}/api/services/${endpoint}`, axiosConfig);
        if (response.data.success && response.data.countries) {
          setAvailableCountries(response.data.countries);
          setAvailableServices([]); // Services loaded after country selection
        }
      } else {
        // Server 2/3 - old format
        const response = await axios.get(`${API}/api/services/${endpoint}`, axiosConfig);
        if (response.data.success) {
          const data = response.data.data;
          const services = [];
          const countries = [];
          
          for (const key1 in data) {
            for (const key2 in data[key1]) {
              const serviceData = data[key1][key2];
              if (!services.find(s => s.value === key2)) {
                services.push({ value: key2, label: serviceData.name || key2 });
              }
              if (!countries.find(c => c.value === key1)) {
                countries.push({ value: key1, label: key1.toUpperCase() });
              }
            }
          }
          
          setAvailableServices(services);
          setAvailableCountries(countries);
        }
      }
    } catch (error) {
      console.error('Failed to fetch services:', error);
      toast.error('Failed to load services');
    } finally {
      setServicesLoading(false);
    }
  };

  // Fetch services when country changes (for SMS-pool)
  useEffect(() => {
    const fetchServicesForCountry = async () => {
      if (selectedServer?.value === 'server1' && selectedCountry) {
        setServicesLoading(true);
        try {
          const response = await axios.get(
            `${API}/api/services/server1?country=${selectedCountry.value}`,
            axiosConfig
          );
          
          if (response.data.success && response.data.services) {
            setAvailableServices(response.data.services);
          }
        } catch (error) {
          console.error('Failed to fetch services for country:', error);
          toast.error('Failed to load services');
        } finally {
          setServicesLoading(false);
        }
      }
    };

    fetchServicesForCountry();
  }, [selectedCountry]);

  // Check if ALL bills are disabled via master toggle
  const allBillsDisabled = pageToggles.disable_all_bills === true;

  const allMenuItems = [
    {
      category: 'OVERVIEW',
      items: [
        { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { id: 'transactions', icon: Receipt, label: 'Transactions' }
      ]
    },
    {
      category: 'SERVICES',
      items: [
        { id: 'fund-wallet', icon: Wallet, label: 'Fund Wallet', toggle: 'enable_fund_wallet' },
        { id: 'convert-currency', icon: RefreshCw, label: 'Convert Currency' },
        { id: 'virtual-numbers', icon: Phone, label: 'Virtual Numbers', toggle: 'enable_virtual_numbers' },
        { id: 'giftcards', icon: Gift, label: 'Gift Cards', badge: 'NEW', toggle: 'enable_giftcards' },
        ...(allBillsDisabled ? [] : [
          { id: 'airtime', icon: Phone, label: 'Airtime', toggle: 'enable_airtime' },
          { id: 'bills-payment', icon: Receipt, label: 'Bills Payment', badge: 'NEW', toggle: 'enable_bills' },
        ]),
        { id: 'virtual-cards', icon: CreditCard, label: 'Virtual Cards', toggle: 'enable_virtual_cards' },
        // Travel is only shown on sites that have it enabled (site-specific feature)
        ...(showTravelFeature ? [{ id: 'travel', icon: Plane, label: 'Travel', badge: 'NEW', toggle: 'enable_travel_booking' }] : [])
      ]
    },
    {
      category: 'RESELLER',
      items: [
        { id: 'reseller', icon: Server, label: 'Reseller Portal', badge: 'API' }
      ]
    },
    {
      category: 'MANAGEMENT',
      items: [
        { id: 'sms-history', icon: History, label: 'SMS History' },
        { id: 'referral', icon: Gift, label: 'Referral Program', toggle: 'enable_referral' },
        { id: 'profile', icon: UserCircle, label: 'Profile Settings' },
        { id: 'support', icon: MessageSquare, label: 'Support Channels' }
      ]
    }
  ];

  // Filter menu items based on page toggles

  const isPageEnabled = (id) => {
    const map = {
      dashboard: 'enable_dashboard',
      transactions: 'enable_transactions',
      'fund-wallet': 'enable_fund_wallet',
      'virtual-numbers': 'enable_virtual_numbers',
      'buy-data': 'enable_buy_data',
      airtime: 'enable_airtime',
      'bills-payment': 'enable_bills',
      'virtual-cards': 'enable_virtual_cards',
      'sms-history': 'enable_sms_history',
      'account-upgrade': 'enable_account_upgrade',
      referral: 'enable_referral',
      profile: 'enable_profile',
      support: 'enable_support'
    };
    const key = map[id];
    if (!key) return true;
    return pageToggles[key] !== false;
  };

  const maintenanceContent = (title = 'Maintenance in progress') => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-foreground">{title}</h2>
      <div className="bg-card p-8 rounded-lg border shadow-sm text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <Settings className="w-7 h-7 text-primary" />
        </div>
        <p className="text-muted-foreground font-semibold">Maintenance in progress</p>
        <p className="text-sm text-muted-foreground mt-1">This page is currently disabled by the admin.</p>
      </div>
    </div>
  );

  // NOTE: you requested: keep pages visible but show maintenance if OFF.
  // So we do NOT filter them out; we only use toggles to block content/actions.
  const menuItems = allMenuItems;

  // ---------------------------------------------------------------------------
  // Mobile bottom tab bar (design handoff: BOTTOM TAB BAR, mobile only).
  // Mirrors the sidebar's behaviour exactly: it never filters items out, it
  // just switches activeSection. Disabled pages still render maintenanceContent
  // via isPageEnabled(), same as every other entry point.
  // ---------------------------------------------------------------------------
  const bottomTabs = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Home' },
    { id: 'transactions', icon: History, label: 'History' },
    { id: 'fund-wallet', icon: Wallet, label: 'Wallet' },
    { id: 'virtual-numbers', icon: Phone, label: 'Numbers' },
    { id: 'profile', icon: UserCircle, label: 'Profile' }
  ];

  const BottomTabBar = () => (
    <nav className="fixed bottom-0 inset-x-0 z-30 lg:hidden">
      <div className="bg-card/95 backdrop-blur border-t border-border grid grid-cols-5 pb-safe">
        {bottomTabs.map((tab) => {
          const active = activeSection === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => {
                setActiveSection(tab.id);
                setSidebarOpen(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              aria-current={active ? 'page' : undefined}
              className={`flex flex-col items-center gap-1 py-2.5 text-[11px] font-semibold transition-colors ${
                active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );

  // Main Dashboard Render
  const renderDashboard = () => (
    <div className="min-h-screen bg-muted/50 flex">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Clean white design with reduced font sizes */}
      <aside className={`
        fixed lg:sticky top-0 left-0 h-screen z-50
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        w-72 lg:w-64 bg-card border-r border-border transition-transform duration-300 ease-in-out
        shadow-2xl lg:shadow-none flex flex-col
      `}>
        <div className="p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Logo - use image if available */}
              {branding.brand_logo_url ? (
                <img src={branding.brand_logo_url} alt="Logo" className="h-14 sm:h-16 lg:h-20 object-contain" />
              ) : (
                <div className="w-10 h-10 rounded-md flex items-center justify-center shadow" style={{ backgroundColor: branding.primary_color_hex || '#059669' }}>
                  <Phone className="w-5 h-5 text-white" />
                </div>
              )}
            </div>
            {/* Close button for mobile */}
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1.5 hover:bg-muted rounded-md"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>

        <nav className="px-2 space-y-4 pb-28 flex-1 overflow-y-auto">
          {menuItems.map((section, idx) => (
            <div key={idx}>
              <p className="px-3 text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5">{section.category}</p>
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveSection(item.id);
                      setSidebarOpen(false); // Close sidebar on mobile after selection
                    }}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-md transition-colors ${
                      activeSection === item.id
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <item.icon className="w-4 h-4 shrink-0" />
                      <span className="text-sm font-semibold truncate">{item.label}</span>
                    </div>
                    {item.badge && (
                      <span className="shrink-0 px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-accent text-accent-foreground">
                        {item.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-border space-y-1 bg-card">
          {user.is_admin && (
            <a
              href="/admin"
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors font-semibold text-sm"
            >
              <Settings className="w-4 h-4" />
              <span>Admin Panel</span>
            </a>
          )}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-destructive hover:bg-destructive/10 transition-colors font-semibold text-sm"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-0">
        {/* Top Bar - Clean white design */}
        <header className="sticky top-0 z-30 bg-background/90 backdrop-blur border-b border-border px-4 lg:px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Hamburger menu for mobile */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <Menu className="w-6 h-6 text-muted-foreground" />
            </button>
            
            {/* Desktop: Welcome text */}
            <div className="hidden lg:block">
              <p className="text-sm text-muted-foreground">Welcome back,</p>
              <p className="text-base font-semibold text-foreground">{user.full_name || user.email?.split('@')[0] || 'User'}</p>
            </div>
            
            <div className="flex items-center gap-2 lg:gap-3">
              {/* Refresh data button */}
              <button 
                onClick={() => {
                  fetchProfile();
                  fetchOrders();
                  fetchNotifications();
                  fetchTransactions();
                  toast.success('Data refreshed!');
                }}
                className="p-2.5 hover:bg-muted rounded-lg transition-colors flex items-center justify-center" 
                title="Refresh data"
              >
                <RefreshCw className="w-5 h-5 text-muted-foreground hover:text-muted-foreground" />
              </button>

              {/* Dark mode toggle */}
              <button 
                onClick={toggleDarkMode}
                className="hidden sm:flex p-2.5 hover:bg-muted rounded-lg transition-colors items-center justify-center" 
                title="Toggle theme"
              >
                {darkMode ? (
                  <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              {/* Notifications bell with dropdown */}
              <div className="relative">
                <button 
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2.5 hover:bg-muted rounded-lg transition-colors" 
                  title="Notifications"
                >
                  <Bell className="w-5 h-5 text-muted-foreground" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 bg-destructive text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div className="fixed right-4 top-16 sm:absolute sm:right-0 sm:top-auto mt-2 w-[calc(100vw-2rem)] sm:w-80 max-w-sm bg-card rounded-lg shadow-xl border border-border z-[100] max-h-96 overflow-hidden">
                    <div className="p-3 sm:p-4 border-b border-border flex items-center justify-between">
                      <h3 className="font-semibold text-foreground text-sm sm:text-base">Notifications</h3>
                      <button 
                        onClick={() => setShowNotifications(false)}
                        className="p-1.5 hover:bg-muted rounded-md transition-colors"
                      >
                        <X className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </div>
                    <div className="overflow-y-auto max-h-72">
                      {notifications.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground text-sm">
                          No notifications yet
                        </div>
                      ) : (
                        notifications.slice(0, 10).map((notif) => (
                          <div 
                            key={notif.id}
                            className={`p-3 sm:p-4 border-b border-border hover:bg-muted/50 cursor-pointer transition-colors ${!notif.read_at ? 'bg-primary/10' : ''}`}
                            onClick={() => markNotificationRead(notif.id)}
                          >
                            <div className="flex items-start gap-2 sm:gap-3">
                              <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: !notif.read_at ? branding.primary_color_hex || '#059669' : 'hsl(var(--border))' }}></div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs sm:text-sm font-medium text-foreground">{notif.title}</p>
                                <p className="text-[10px] sm:text-xs text-muted-foreground mt-1 line-clamp-2">{notif.message}</p>
                                <p className="text-[10px] text-muted-foreground mt-1.5">
                                  {new Date(notif.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  dismissNotification(notif.id);
                                }}
                                className="p-1 hover:bg-muted rounded-md text-muted-foreground hover:text-muted-foreground transition-colors"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Refresh Balance Button */}
              <button
                onClick={fetchProfile}
                className="p-2.5 rounded-lg hover:bg-muted transition-colors"
                title="Refresh balance"
                data-testid="header-refresh-btn"
              >
                <RefreshCw className="w-5 h-5 text-muted-foreground" />
              </button>
              
              {/* User avatar - Modern design with dynamic color */}
              <div 
                className="hidden sm:flex w-10 h-10 rounded-lg items-center justify-center text-white font-semibold text-sm shadow-lg"
                style={{ backgroundColor: branding.primary_color_hex || '#059669' }}
              >
                {user.email?.slice(0, 2).toUpperCase() || 'U'}
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="px-4 lg:px-6 py-4 lg:py-6 mb-tabbar lg:mb-0">
          {/* Account Suspension Banner */}
          {user.is_suspended && (
            <div className="mb-6 p-6 bg-gradient-to-r from-red-50 to-orange-50 border-2 border-destructive/30 rounded-lg shadow-sm">
              <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-destructive/10 rounded-full">
                    <Shield className="w-8 h-8 text-destructive" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-destructive">Account Suspended</h3>
                    <p className="text-sm text-destructive mt-1">
                      {user.suspension_reason || 'Your account has been suspended. Please upgrade your KYC tier to continue using BillHub.'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setActiveSection('profile')}
                  className="lg:ml-auto px-6 py-2.5 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-lg font-medium hover:from-red-700 hover:to-orange-700 transition-all shadow-md hover:shadow-lg flex items-center gap-2"
                  data-testid="upgrade-kyc-btn"
                >
                  <Shield className="w-4 h-4" />
                  Upgrade KYC Now
                </button>
              </div>
              <div className="mt-4 p-3 bg-card/50 rounded-md border border-destructive/30">
                <p className="text-xs text-destructive">
                  <strong>Why was my account suspended?</strong> Your wallet balance has exceeded the maximum limit for your current KYC tier. 
                  To unlock your account and increase your limits, please verify your identity by upgrading to a higher KYC tier.
                </p>
              </div>
            </div>
          )}

          <div className="w-full">
            {activeSection === 'virtual-numbers' && (
              isPageEnabled('virtual-numbers') ? (
                <VirtualNumbersSection
                  user={user}
                  orders={orders}
                  axiosConfig={axiosConfig}
                  fetchOrders={fetchOrders}
                  fetchProfile={fetchProfile}
                />
              ) : (
                maintenanceContent("Virtual Numbers")
              )
            )}

            {activeSection === 'fund-wallet' && (isPageEnabled('fund-wallet') ? (
              <FundWalletSection 
                user={user}
                axiosConfig={axiosConfig}
                fetchProfile={fetchProfile}
                pageToggles={pageToggles}
                transactions={transactions}
              />
            ) : maintenanceContent("Fund Wallet"))}
            {activeSection === 'airtime' && (isPageEnabled('airtime') ? <AirtimeSection axiosConfig={axiosConfig} fetchProfile={fetchProfile} fetchTransactions={fetchTransactions} /> : maintenanceContent("Airtime"))}
            {activeSection === 'bills-payment' && (isPageEnabled('bills-payment') ? <BillsPaymentSection axiosConfig={axiosConfig} fetchProfile={fetchProfile} fetchTransactions={fetchTransactions} user={user} setActiveSection={setActiveSection} primaryColor={branding.primary_color_hex || '#059669'} pageToggles={pageToggles} defaultTab={defaultBillTab} /> : maintenanceContent("Bills Payment"))}
            {activeSection === 'transactions' && (isPageEnabled('transactions') ? <TransactionsSection /> : maintenanceContent("Transactions"))}
            {activeSection === 'dashboard' && (isPageEnabled('dashboard') ? <DashboardOverview /> : maintenanceContent("Dashboard"))}
            {activeSection === 'sms-history' && (isPageEnabled('sms-history') ? <SMSHistorySection /> : maintenanceContent("SMS History"))}
            {activeSection === 'profile' && (isPageEnabled('profile') ? <ProfileSection /> : maintenanceContent("Profile Settings"))}
            {activeSection === 'referral' && (isPageEnabled('referral') ? <ReferralSection /> : maintenanceContent("Referral Program"))}
            {activeSection === 'support' && (isPageEnabled('support') ? <SupportSection /> : maintenanceContent("Support Channels"))}
            {activeSection === 'virtual-cards' && (isPageEnabled('virtual-cards') ? <VirtualCardsSection axiosConfig={axiosConfig} fetchProfile={fetchProfile} user={user} primaryColor={branding.primary_color_hex || '#059669'} branding={branding} onNavigateToKYC={() => setActiveSection('profile')} /> : maintenanceContent("Virtual Cards"))}
            {activeSection === 'giftcards' && (isPageEnabled('giftcards') ? <GiftCardsSection /> : maintenanceContent("Gift Cards"))}
            {activeSection === 'travel' && <TravelSection axiosConfig={axiosConfig} fetchProfile={fetchProfile} user={user} primaryColor={branding.primary_color_hex || '#059669'} branding={branding} />}
            {activeSection === 'convert-currency' && <ConvertCurrencySection />}
            {activeSection === 'reseller' && <ResellerSection />}
          </div>
        </main>
      </div>

      <BottomTabBar />
    </div>
  );

  // NOTE: VirtualNumbersSection has been extracted into
  // ../components/VirtualNumbersSection.js to prevent remounts
  // that caused dropdowns to close while typing.

  function DashboardOverview() {
    const [bannerIndex, setBannerIndex] = useState(0);
    
    // Get banner images from branding (admin editable) or use defaults
    const bannerImages = (branding.banner_images && branding.banner_images.length > 0 
      ? branding.banner_images.filter(b => b.active !== false)
      : [
        { id: '1', image_url: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=300&fit=crop', link: '' },
        { id: '2', image_url: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=300&fit=crop', link: '' },
        { id: '3', image_url: 'https://images.unsplash.com/photo-1518458028785-8fbcd101ebb9?w=800&h=300&fit=crop', link: '' }
      ]).map(b => ({
        id: b.id,
        image: b.image_url,
        link: b.link || '',
        action: b.link ? () => window.open(b.link, '_blank') : () => setActiveSection('fund-wallet')
      }));

    // Auto-rotate banners
    useEffect(() => {
      if (bannerImages.length <= 1) return;
      const interval = setInterval(() => {
        setBannerIndex((prev) => (prev + 1) % bannerImages.length);
      }, 5000);
      return () => clearInterval(interval);
    }, [bannerImages.length]);

    // Service cards with dynamic colors from branding
    const primaryColor = branding.primary_color_hex || '#059669';
    
    // Country flags component for Virtual Numbers card
    const CountryFlags = () => (
      <div className="flex -space-x-1.5">
        <img src="https://flagcdn.com/w40/us.png" alt="USA" className="w-5 h-5 rounded-full border border-card object-cover" />
        <img src="https://flagcdn.com/w40/gb.png" alt="UK" className="w-5 h-5 rounded-full border border-card object-cover" />
        <img src="https://flagcdn.com/w40/ng.png" alt="Nigeria" className="w-5 h-5 rounded-full border border-card object-cover" />
      </div>
    );

    // Check if all bills disabled from parent scope
    const billsHidden = pageToggles.disable_all_bills === true;
    
    // Check individual bill toggles
    const dataHidden = pageToggles.disable_data === true;
    const tvHidden = pageToggles.disable_tv === true;
    const electricityHidden = pageToggles.disable_electricity === true;
    const airtimeHidden = pageToggles.disable_airtime_bills === true;
    
    const allServiceCards = [
      { name: 'Virtual Numbers', action: () => setActiveSection('virtual-numbers'), icon: CountryFlags, bgColor: 'bg-card', iconBg: 'bg-transparent', isCustomIcon: true, isBill: false, hidden: false },
      { name: 'Internet Data', action: () => { setDefaultBillTab('data'); setActiveSection('bills-payment'); }, bgColor: 'bg-card', iconBg: 'bg-primary/10 text-primary', isBill: true, hidden: dataHidden, icon: () => (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
        </svg>
      )},
      { name: 'TV Sub', action: () => { setDefaultBillTab('tv'); setActiveSection('bills-payment'); }, bgColor: 'bg-card', iconBg: 'bg-primary/10 text-primary', isBill: true, hidden: tvHidden, icon: () => (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      )},
      { name: 'Airtime', action: () => setActiveSection('airtime'), bgColor: 'bg-card', iconBg: 'bg-primary/10 text-primary', isBill: true, hidden: airtimeHidden, icon: () => (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      )},
      { name: 'Electricity', action: () => { setDefaultBillTab('electricity'); setActiveSection('bills-payment'); }, bgColor: 'bg-card', iconBg: 'bg-primary/10 text-primary', isBill: true, hidden: electricityHidden, icon: () => (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )},
      { name: 'Virtual Cards', action: () => setActiveSection('virtual-cards'), icon: CreditCard, bgColor: 'bg-card', iconBg: 'bg-primary/10 text-primary', isBill: false, hidden: false },
    ];

    // Filter out bill services if disabled (master toggle or individual)
    const serviceCards = billsHidden 
      ? allServiceCards.filter(s => !s.isBill)
      : allServiceCards.filter(s => !s.hidden);

    return (
      <div className="space-y-5 sm:space-y-6">
        {/* Top Row: Balance Card + My Card (Desktop) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          {/* Balance Card - Solid gradient background */}
          <div 
            className="lg:col-span-2 relative flex min-h-[240px] flex-col gap-6 overflow-hidden rounded-3xl bg-primary p-6 text-primary-foreground shadow-hero"
          >
            <span className="pointer-events-none absolute inset-0 bg-gradient-to-br from-transparent to-black/25"></span>
            <span className="pointer-events-none absolute -right-14 -top-14 size-52 rounded-full bg-primary-foreground/15"></span>
            <span className="pointer-events-none absolute -bottom-24 right-16 size-48 rounded-full bg-primary-foreground/10"></span>
            {/* Decorative circles */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
            
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-white/80">Total Balance</h3>
                {/* Currency Toggle - pushed to right */}
                <div className="flex rounded-full bg-primary-foreground/15 p-[3px]">
                  <button 
                    onClick={() => setDashboardCurrency('NGN')}
                    className={`px-3 py-1.5 rounded-md font-medium text-xs transition-all ${
                      dashboardCurrency === 'NGN' ? 'rounded-full bg-primary-foreground text-primary' : 'text-white/80 hover:text-white'
                    }`}
                  >
                    NGN
                  </button>
                  <button 
                    onClick={() => setDashboardCurrency('USD')}
                    className={`px-3 py-1.5 rounded-md font-medium text-xs transition-all ${
                      dashboardCurrency === 'USD' ? 'rounded-full bg-primary-foreground text-primary' : 'text-white/80 hover:text-white'
                    }`}
                  >
                    USD
                  </button>
                </div>
              </div>

              <div className="flex items-end justify-between mb-5">
                <div>
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
                    {dashboardCurrency === 'NGN' 
                      ? `₦${(user.ngn_balance || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` 
                      : `$${(user.usd_balance || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}
                    <span className="text-sm font-normal text-white/60 ml-2">{dashboardCurrency}</span>
                  </h1>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-white/20 text-white">+0.00%</span>
                    <span className="text-xs text-white/60">vs previous month</span>
                  </div>
                </div>
                <div className="hidden sm:flex gap-3">
                  <button 
                    onClick={() => setActiveSection('fund-wallet')}
                    className="flex h-12 items-center justify-between whitespace-nowrap rounded-xl bg-primary-foreground px-3.5 text-sm font-bold text-primary"
                  >
                    <Plus className="w-4 h-4" />
                    Add money
                  </button>
                  <button 
                    onClick={() => setActiveSection('convert-currency')}
                    className="flex h-12 items-center justify-between rounded-xl border border-primary-foreground/30 bg-primary-foreground/15 px-3.5 text-sm font-bold"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Convert
                  </button>
                </div>
              </div>

              {/* Mobile button */}
              <div className="grid grid-cols-2 sm:hidden gap-3 mt-4">
                <button 
                  onClick={() => setActiveSection('fund-wallet')}
                  className="flex h-12 items-center justify-between whitespace-nowrap rounded-xl bg-primary-foreground px-3.5 text-sm font-bold text-primary"
                >
                  <Plus className="w-4 h-4" />
                  Add money
                </button>
                <button 
                  onClick={() => setActiveSection('convert-currency')}
                  className="flex h-12 items-center justify-between rounded-xl border border-primary-foreground/30 bg-primary-foreground/15 px-3.5 text-sm font-bold"
                >
                  <RefreshCw className="w-4 h-4" />
                  Convert
                </button>
              </div>
            </div>
          </div>

          {/* My Card - Hidden on mobile, visible on desktop */}
          <div className="hidden lg:block bg-card rounded-lg p-5 sm:p-6 border border-border shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-muted-foreground">My Cards</h3>
              <button 
                onClick={() => setActiveSection('virtual-cards')}
                className="text-xs font-semibold text-primary hover:underline"
              >
                Manage Cards
              </button>
            </div>

            {/* Gradient Card Visual - using team green color */}
            <div className="relative flex min-h-[150px] flex-1 flex-col justify-between overflow-hidden rounded-[20px] bg-gradient-to-br from-neutral-900 to-neutral-700 p-[18px] text-neutral-100 shadow-lift"><span className="pointer-events-none absolute -right-10 -top-10 size-40 rounded-full bg-primary/50"></span>
              {/* Card pattern overlay */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
              </div>
              
              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-white/80 text-xs font-medium">Debit</span>
                  <span className="text-white/80 text-xs">•••• 1234</span>
                </div>
                <div className="mb-4">
                  <p className="text-2xl sm:text-3xl font-bold">
                    ${(user.usd_balance || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </p>
                  <p className="text-white/70 text-xs mt-1">Your Balance</p>
                </div>
                <div className="flex items-center justify-between">
                  {/* Logo image instead of text */}
                  {branding.brand_logo_url ? (
                    <img src={branding.brand_logo_url} alt="Logo" className="h-6 object-contain" />
                  ) : (
                    <span className="text-white/90 font-semibold text-lg">{branding.brand_name || 'UltraCloud'}</span>
                  )}
                  <div className="flex -space-x-2">
                    <div className="w-8 h-8 rounded-full bg-white/30"></div>
                    <div className="w-8 h-8 rounded-full bg-white/20"></div>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-center text-xs text-muted-foreground mt-3">1/1 Cards</p>
          </div>
        </div>

        {/* Banner Carousel */}
        {bannerImages.length > 0 && (
        <div className="relative overflow-hidden rounded-lg">
          <div 
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${bannerIndex * 100}%)` }}
          >
            {bannerImages.map((banner) => (
              <div 
                key={banner.id}
                className="min-w-full cursor-pointer"
                onClick={banner.action}
              >
                <img 
                  src={banner.image} 
                  alt="Banner"
                  className="w-full h-32 sm:h-40 lg:h-48 object-cover rounded-lg"
                />
              </div>
            ))}
          </div>
          {/* Dots indicator */}
          {bannerImages.length > 1 && (
          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-2">
            {bannerImages.map((_, i) => (
              <button
                key={i}
                onClick={() => setBannerIndex(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === bannerIndex ? 'bg-white w-6' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
          )}
        </div>
        )}

        {/* Quick Services - Grid Cards with dynamic color */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-[11px] font-semibold uppercase tracking-[.08em] text-muted-foreground">Quick services</p>
          </div>

          <div className="mt-2.5 grid grid-cols-2 gap-3.5 sm:grid-cols-3 lg:grid-cols-6">
            {serviceCards.map((service, index) => (
              <button 
                key={index}
                onClick={service.action}
                type="button"
                className="rounded-2xl bg-card shadow-card flex min-h-[112px] flex-col items-start justify-between gap-4 p-4 transition hover:-translate-y-0.5 hover:shadow-lift"
              >
                <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                  {service.isCustomIcon ? (
                    <service.icon />
                  ) : (
                    typeof service.icon === 'function'
                      ? <service.icon />
                      : <service.icon className="w-5 h-5" />
                  )}
                </span>
                <span className="text-sm font-extrabold text-left leading-tight text-foreground">
                  {service.name}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-card rounded-lg p-5 sm:p-6 border border-border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-bold text-foreground">Recent Transactions</h3>
            <button 
              onClick={() => setActiveSection('transactions')}
              className="text-xs font-semibold text-primary hover:underline"
            >
              View all
            </button>
          </div>
          
          {transactions.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center mx-auto mb-3">
                <Receipt className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground text-sm">No transactions yet</p>
              <p className="text-muted-foreground text-xs mt-1">Your transaction history will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.slice(0, 5).map((tx, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      tx.type === 'deposit' ? 'bg-primary/10 text-primary' :
                      tx.type === 'purchase' ? 'bg-primary/10 text-primary' :
                      tx.type === 'refund' ? 'bg-amber-50 text-amber-600' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {tx.type === 'deposit' ? <Plus className="w-5 h-5" /> :
                       tx.type === 'purchase' ? <Phone className="w-5 h-5" /> :
                       tx.type === 'refund' ? <RefreshCw className="w-5 h-5" /> :
                       <Wallet className="w-5 h-5" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground capitalize">{tx.type}</p>
                      <p className="text-xs text-muted-foreground">{new Date(tx.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-bold ${tx.type === 'deposit' || tx.type === 'refund' ? 'text-primary' : 'text-foreground'}`}>
                      {tx.type === 'deposit' || tx.type === 'refund' ? '+' : '-'}
                      {tx.currency === 'NGN' ? '₦' : '$'}{tx.amount?.toLocaleString() || '0'}
                    </p>
                    <p className={`text-xs ${tx.status === 'completed' ? 'text-primary' : 'text-amber-600'}`}>
                      {tx.status}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  function TransactionsSection() {
    const [filterType, setFilterType] = useState('all');
    const [filterCurrency, setFilterCurrency] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [searchText, setSearchText] = useState('');
    const [selectedTxn, setSelectedTxn] = useState(null);

    // Calculate summary statistics
    const totalTransactions = transactions.length;
    const creditTotal = transactions
      .filter(t => t.type === 'credit')
      .reduce((sum, t) => sum + (t.amount || 0), 0);
    const debitTotal = transactions
      .filter(t => t.type === 'debit')
      .reduce((sum, t) => sum + (t.amount || 0), 0);
    const netAmount = creditTotal - debitTotal;

    // Transaction Details Modal
    const TransactionDetailsModal = ({ txn, onClose }) => {
      if (!txn) return null;
      
      const isCredit = txn.type === 'credit' || txn.type === 'deposit_ngn' || txn.type === 'deposit_usd' || txn.type === 'currency_conversion';
      
      return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
          <div className="bg-card rounded-lg max-w-md w-full shadow-xl" onClick={e => e.stopPropagation()}>
            <div className={`p-4 rounded-t-xl ${isCredit ? 'bg-gradient-to-r from-emerald-500 to-primary' : 'bg-primary'} text-white`}>
              <h3 className="font-bold text-lg">Transaction Details</h3>
              <p className="text-white/80 text-sm">{txn.type?.replace(/_/g, ' ').toUpperCase()}</p>
            </div>
            <div className="p-4 space-y-4">
              {/* Amount */}
              <div className="text-center py-4 border-b">
                <p className="text-muted-foreground text-sm">Amount</p>
                <p className={`text-3xl font-bold ${isCredit ? 'text-primary' : 'text-primary'}`}>
                  {isCredit ? '+' : '-'}{txn.currency === 'NGN' ? '₦' : '$'}{txn.amount?.toLocaleString()}
                </p>
              </div>
              
              {/* Balance Before & After */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/50 p-3 rounded-md">
                  <p className="text-xs text-muted-foreground">Balance Before</p>
                  <p className="font-semibold text-foreground">
                    {txn.currency === 'NGN' ? '₦' : '$'}
                    {txn.balance_before !== undefined ? txn.balance_before.toLocaleString() : 'N/A'}
                  </p>
                </div>
                <div className="bg-muted/50 p-3 rounded-md">
                  <p className="text-xs text-muted-foreground">Balance After</p>
                  <p className="font-semibold text-foreground">
                    {txn.currency === 'NGN' ? '₦' : '$'}
                    {txn.balance_after !== undefined ? txn.balance_after.toLocaleString() : 'N/A'}
                  </p>
                </div>
              </div>
              
              {/* Details */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Transaction ID</span>
                  <span className="font-medium text-foreground">{txn.id || txn._id || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Reference</span>
                  <span className="font-medium text-foreground">{txn.reference || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Status</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                    txn.status === 'completed' ? 'bg-primary/10 text-primary' :
                    txn.status === 'pending' ? 'bg-amber-50 text-amber-600' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {txn.status}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Date</span>
                  <span className="font-medium text-foreground">{new Date(txn.created_at).toLocaleString()}</span>
                </div>
                {txn.description && (
                  <div className="py-2">
                    <span className="text-muted-foreground block mb-1">Description</span>
                    <span className="font-medium text-foreground text-xs">{txn.description}</span>
                  </div>
                )}
              </div>
              
              <button 
                onClick={onClose}
                className="w-full py-2 bg-muted text-muted-foreground rounded-md hover:bg-muted font-medium text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="mb-7 flex flex-wrap items-end justify-between gap-4 border-b border-border pb-5">
              <div><p className="text-sm text-muted-foreground">Track your wallet activity</p><h1 className="mt-1 text-3xl font-extrabold leading-tight tracking-tight lg:text-[38px]">Transactions</h1></div>
            </div>
          </div>
          <button className="flex items-center justify-center gap-2 px-4 py-2 border border-border rounded-md hover:bg-muted/50 transition-colors text-sm">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export
          </button>
        </div>

        {/* Filters */}
        <div className="rounded-2xl bg-card shadow-card p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <h3 className="font-semibold text-foreground text-sm sm:text-base">Filter Transactions</h3>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4 mb-4">
            {/* Type Filter */}
            <div>
              <label className="block text-xs sm:text-sm text-muted-foreground mb-1 sm:mb-2">Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-2 sm:px-3 py-2 border border-border rounded-md focus:outline-none focus:border-primary text-xs sm:text-sm"
              >
                <option value="all">All Types</option>
                <option value="credit">Credit</option>
                <option value="debit">Debit</option>
              </select>
            </div>

            {/* Currency Filter */}
            <div>
              <label className="block text-xs sm:text-sm text-muted-foreground mb-1 sm:mb-2">Currency</label>
              <select
                value={filterCurrency}
                onChange={(e) => setFilterCurrency(e.target.value)}
                className="w-full px-2 sm:px-3 py-2 border border-border rounded-md focus:outline-none focus:border-primary text-xs sm:text-sm"
              >
                <option value="all">All</option>
                <option value="NGN">NGN</option>
                <option value="USD">USD</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-xs sm:text-sm text-muted-foreground mb-1 sm:mb-2">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-2 sm:px-3 py-2 border border-border rounded-md focus:outline-none focus:border-primary text-xs sm:text-sm"
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            {/* From Date */}
            <div>
              <label className="block text-xs sm:text-sm text-muted-foreground mb-1 sm:mb-2">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-2 sm:px-3 py-2 border border-border rounded-md focus:outline-none focus:border-primary text-xs sm:text-sm"
              />
            </div>

            {/* To Date */}
            <div>
              <label className="block text-xs sm:text-sm text-muted-foreground mb-1 sm:mb-2">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-2 sm:px-3 py-2 border border-border rounded-md focus:outline-none focus:border-primary text-xs sm:text-sm"
              />
            </div>
          </div>

          {/* Search */}
          <div>
            <label className="block text-xs sm:text-sm text-muted-foreground mb-1 sm:mb-2">Search</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search..."
                className="flex-1 min-w-0 px-2 sm:px-3 py-2 border border-border rounded-md focus:outline-none focus:border-primary text-xs sm:text-sm"
              />
              <button className="px-3 sm:px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-shrink-0">
                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span className="hidden sm:inline">Apply</span>
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
          {/* Total */}
          <div className="bg-card rounded-md sm:rounded-lg border shadow-sm p-3 sm:p-6">
            <p className="text-[10px] sm:text-sm text-muted-foreground mb-1 sm:mb-2">TOTAL</p>
            <p className="text-lg sm:text-3xl font-bold text-foreground">{totalTransactions}</p>
          </div>

          {/* Credit */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-md sm:rounded-lg border border-primary shadow-sm p-3 sm:p-6">
            <p className="text-[10px] sm:text-sm text-primary mb-1 sm:mb-2">CREDIT</p>
            <p className="text-lg sm:text-3xl font-bold text-primary">₦{creditTotal.toFixed(0)}</p>
          </div>

          {/* Debit */}
          <div className="bg-gradient-to-br from-red-50 to-primary/10 rounded-md sm:rounded-lg border border-destructive/30 shadow-sm p-3 sm:p-6">
            <p className="text-[10px] sm:text-sm text-destructive mb-1 sm:mb-2">DEBIT</p>
            <p className="text-lg sm:text-3xl font-bold text-red-900">₦{debitTotal.toFixed(0)}</p>
          </div>

          {/* Net */}
          <div className="bg-card rounded-md sm:rounded-lg border shadow-sm p-3 sm:p-6">
            <p className="text-[10px] sm:text-sm text-muted-foreground mb-1 sm:mb-2">NET</p>
            <p className="text-lg sm:text-3xl font-bold text-foreground">₦{netAmount.toFixed(0)}</p>
          </div>
        </div>

        {/* Transactions Table or Empty State */}
        <div className="bg-card rounded-md sm:rounded-lg border shadow-sm p-3 sm:p-6">
          {transactions.length > 0 ? (
            <>
              {/* Mobile Card View */}
              <div className="block sm:hidden space-y-3">
                {transactions.map((txn) => (
                  <div key={txn.id} className="border rounded-md p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className={`px-2 py-0.5 text-[10px] font-semibold rounded ${
                          txn.type === 'credit' || txn.type === 'deposit_ngn' || txn.type === 'deposit_usd'
                            ? 'bg-primary/10 text-primary' 
                            : 'bg-destructive/10 text-destructive'
                        }`}>
                          {txn.type.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      <span className={`font-bold text-sm ${
                        txn.type === 'credit' || txn.type === 'deposit_ngn' || txn.type === 'deposit_usd'
                          ? 'text-primary' 
                          : 'text-destructive'
                      }`}>
                        {txn.type === 'credit' || txn.type === 'deposit_ngn' || txn.type === 'deposit_usd' ? '+' : '-'}
                        {txn.currency === 'NGN' ? '₦' : '$'}{txn.amount?.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-[10px] text-muted-foreground">{new Date(txn.created_at).toLocaleDateString()}</div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                          txn.status === 'completed' ? 'bg-primary/10 text-primary' :
                          txn.status === 'pending' ? 'bg-amber-50 text-amber-600' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {txn.status}
                        </span>
                        <button 
                          onClick={() => setSelectedTxn(txn)}
                          className="px-2 py-1 text-[10px] font-semibold text-primary hover:bg-primary/10 rounded"
                        >
                          View
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Date</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Type</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Amount</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((txn) => (
                      <tr key={txn.id} className="border-b hover:bg-muted/50 transition-colors">
                        <td className="py-4 px-4 text-sm text-muted-foreground">{new Date(txn.created_at).toLocaleDateString()}</td>
                        <td className="py-4 px-4">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-md ${
                            txn.type === 'credit' || txn.type === 'deposit_ngn' || txn.type === 'deposit_usd'
                              ? 'bg-primary/10 text-primary' 
                              : 'bg-destructive/10 text-destructive'
                          }`}>
                            {txn.type.replace('_', ' ').toUpperCase()}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`font-semibold ${
                            txn.type === 'credit' || txn.type === 'deposit_ngn' || txn.type === 'deposit_usd'
                              ? 'text-primary' 
                              : 'text-destructive'
                          }`}>
                            {txn.type === 'credit' || txn.type === 'deposit_ngn' || txn.type === 'deposit_usd' ? '+' : '-'}
                            {txn.currency === 'NGN' ? '₦' : '$'}{txn.amount?.toLocaleString()}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                            txn.status === 'completed' ? 'bg-primary/10 text-primary' :
                            txn.status === 'pending' ? 'bg-amber-50 text-amber-600' :
                            'bg-muted text-muted-foreground'
                          }`}>
                            {txn.status}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <button 
                            onClick={() => setSelectedTxn(txn)}
                            className="px-3 py-1.5 text-xs font-semibold text-primary hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-center py-8 sm:py-16">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-3 sm:mb-4 bg-muted rounded-full flex items-center justify-center">
                <Wallet className="w-8 h-8 sm:w-10 sm:h-10 text-muted-foreground" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2">No Transactions Found</h3>
              <p className="text-sm text-muted-foreground mb-4 sm:mb-6">Your wallet history will appear here</p>
              <button 
                onClick={() => setActiveSection('fund-wallet')}
                className="px-4 sm:px-6 py-2.5 sm:py-3 bg-primary text-white rounded-md font-semibold text-sm hover:bg-primary/90 transition-colors"
              >
                Fund Wallet
              </button>
            </div>
          )}
        </div>
        
        {/* Transaction Details Modal */}
        {selectedTxn && <TransactionDetailsModal txn={selectedTxn} onClose={() => setSelectedTxn(null)} />}
      </div>
    );
  }

  function SMSHistorySection() {
    const getServiceName = (code) => {
      if (!code) return 'Unknown';
      const names = {
        'wa': 'WhatsApp', 'tg': 'Telegram', 'go': 'Google', 'fb': 'Facebook',
        'ig': 'Instagram', 'tw': 'Twitter', 'ds': 'Discord', 'tt': 'TikTok'
      };
      return names[code] || code.toUpperCase();
    };

    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <div className="mb-7 flex flex-wrap items-end justify-between gap-4 border-b border-border pb-5">
              <div><p className="text-sm text-muted-foreground">Track your OTP requests</p><h1 className="mt-1 text-3xl font-extrabold leading-tight tracking-tight lg:text-[38px]">SMS history</h1></div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="px-2 py-1 bg-primary/10 text-primary rounded-md font-medium">{orders.length}</span>
            <span>Total Orders</span>
          </div>
        </div>
        
        {orders.length > 0 ? (
          <div className="bg-card p-3 sm:p-6 rounded-lg border shadow-sm">
            {/* Mobile Card Layout */}
            <div className="block md:hidden space-y-3">
              {orders.map((order) => (
                <div key={order.id} className={`rounded-lg p-3 border ${(order.otp || order.otp_code) ? 'bg-primary/10 border-primary' : 'bg-muted/50 border-border'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center">
                        <span className="text-primary text-xs font-bold">{(order.service || '?')[0].toUpperCase()}</span>
                      </div>
                      <span className="font-medium text-sm text-foreground">{getServiceName(order.service)}</span>
                    </div>
                    <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                      order.status === 'active' || order.status === 'RECEIVED' ? 'bg-primary/10 text-primary' :
                      order.status === 'cancelled' || order.status === 'CANCELED' ? 'bg-destructive/10 text-destructive' :
                      order.status === 'completed' ? 'bg-primary/10 text-primary' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {order.status}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] text-muted-foreground">Phone:</span>
                    <span className="font-mono text-xs text-foreground">{order.phone_number}</span>
                  </div>
                  
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] text-muted-foreground">Code:</span>
                    {order.otp || order.otp_code ? (
                      <span className="font-mono text-lg font-bold text-primary bg-card px-2 py-0.5 rounded">
                        {order.otp || order.otp_code}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs italic">Not received</span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                    <span>{new Date(order.created_at).toLocaleDateString()}</span>
                    <span>{new Date(order.created_at).toLocaleTimeString()}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table Layout */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-black">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Service</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Phone Number</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Code</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id} className="border-b hover:bg-muted/50 transition-colors">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center">
                            <span className="text-primary text-xs font-bold">{(order.service || '?')[0].toUpperCase()}</span>
                          </div>
                          <span className="font-medium text-foreground">{getServiceName(order.service)}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 font-mono text-sm text-foreground">
                        {order.phone_number}
                      </td>
                      <td className="py-4 px-4 text-foreground">
                        {order.otp || order.otp_code ? (
                          <span className="font-mono text-lg font-bold text-primary bg-primary/10 px-2 py-1 rounded-md">
                            {order.otp || order.otp_code}
                          </span>
                        ) : (
                          <span className="text-muted-foreground italic">Not received</span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          order.status === 'active' || order.status === 'RECEIVED' ? 'bg-primary/10 text-primary' :
                          order.status === 'cancelled' || order.status === 'CANCELED' ? 'bg-destructive/10 text-destructive' :
                          order.status === 'completed' ? 'bg-primary/10 text-primary' :
                          order.status === 'pending' || order.status === 'PENDING' ? 'bg-amber-50 text-amber-600' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-sm text-muted-foreground">
                        {new Date(order.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-card p-6 rounded-lg border shadow-sm text-center py-12">
            <History className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No SMS History</h3>
            <p className="text-muted-foreground mb-6">Your OTP request history will appear here</p>
            <button 
              onClick={() => setActiveSection('virtual-numbers')}
              className="px-6 py-3 bg-primary text-white rounded-md font-semibold hover:bg-primary transition-colors"
            >
              Get Virtual Number
            </button>
          </div>
        )}
      </div>
    );
  }

  function ProfileSection() {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [savingPassword, setSavingPassword] = useState(false);
    const [fullName, setFullName] = useState(user.full_name || '');
    const [phone, setPhone] = useState(user.phone || '');
    const [savingProfile, setSavingProfile] = useState(false);
    const [activeTab, setActiveTab] = useState('profile');
    
    // KYC states
    const [bvn, setBvn] = useState('');
    const [nin, setNin] = useState('');
    const [kycPhone, setKycPhone] = useState(user.phone || '');
    const [dob, setDob] = useState('');
    const [address, setAddress] = useState(user.address || '');
    
    // Tier 3 Address fields for Payscribe customer creation
    const [kycStreet, setKycStreet] = useState('');
    const [kycCity, setKycCity] = useState('');
    const [kycState, setKycState] = useState('');
    const [kycPostalCode, setKycPostalCode] = useState('');
    const [kycCountry, setKycCountry] = useState('NG');
    
    // First/Last name for profile (editable only before Tier 2)
    const [firstName, setFirstName] = useState(user.first_name || '');
    const [lastName, setLastName] = useState(user.last_name || '');
    
    // Selfie camera states
    const [showCamera, setShowCamera] = useState(false);
    const [selfieImage, setSelfieImage] = useState(null);
    const [cameraError, setCameraError] = useState('');
    const [isCapturing, setIsCapturing] = useState(false);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    
    // Verification popup states
    const [showVerificationPopup, setShowVerificationPopup] = useState(false);
    const [verificationStep, setVerificationStep] = useState(''); // 'bvn' or 'nin'
    const [verifying, setVerifying] = useState(false);
    const [bvnVerified, setBvnVerified] = useState(user.bvn_verified || false);
    const [ninVerified, setNinVerified] = useState(user.nin_verified || false);
    const [verificationError, setVerificationError] = useState('');
    const [verificationSuccess, setVerificationSuccess] = useState('');
    
    // Edit fields for retry (initialized when error occurs)
    const [editFirstName, setEditFirstName] = useState(user.first_name || '');
    const [editLastName, setEditLastName] = useState(user.last_name || '');
    const [editPhone, setEditPhone] = useState(user.phone || '');
    const [editDob, setEditDob] = useState('');
    
    // Transaction PIN states
    const [hasPin, setHasPin] = useState(user.has_transaction_pin || false);
    const [pinAction, setPinAction] = useState(''); // 'set', 'change', 'reset'
    const [newPin, setNewPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [currentPin, setCurrentPin] = useState('');
    const [resetCode, setResetCode] = useState('');
    const [resetBvn, setResetBvn] = useState('');
    const [savingPin, setSavingPin] = useState(false);
    const [requestingReset, setRequestingReset] = useState(false);

    const primaryColor = branding.primary_color_hex || '#059669';
    const accentColor = branding.accent_color_hex || '#7c3aed';
    const userTier = user.tier || 1;
    const KYC_FEE = branding.kyc_tier3_fee || 200; // Fee for Express KYC (Tier 3 upgrade)

    const handleUpdateProfile = async () => {
      if (!firstName.trim() || !lastName.trim()) {
        toast.error('First and Last name are required');
        return;
      }
      setSavingProfile(true);
      try {
        const fullNameCombined = `${firstName.trim()} ${lastName.trim()}`;
        const response = await axios.put(`${API}/api/user/profile`, { 
          full_name: fullNameCombined, 
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone: phone,
          address: address
        }, axiosConfig);
        setUser({ ...user, full_name: fullNameCombined, first_name: firstName.trim(), last_name: lastName.trim(), phone: phone, address: address });
        toast.success('Profile updated successfully');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update profile');
      } finally {
        setSavingProfile(false);
      }
    };
    
    // Camera functions for selfie capture
    const startCamera = async () => {
      setCameraError('');
      setShowCamera(true);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } } 
        });
        streamRef.current = stream;
        // Attach stream immediately and retry if video not ready
        const attachStream = () => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.onloadedmetadata = () => {
              videoRef.current.play().catch(e => console.log('Video play error:', e));
            };
          } else {
            // Retry after a short delay if ref not ready
            setTimeout(attachStream, 50);
          }
        };
        attachStream();
      } catch (err) {
        console.error('Camera error:', err);
        setCameraError('Camera access denied. Please allow camera access to take a selfie.');
        setShowCamera(false);
      }
    };
    
    const stopCamera = () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      setShowCamera(false);
    };
    
    const captureSelfie = () => {
      setIsCapturing(true);
      setTimeout(() => {
        if (videoRef.current && canvasRef.current) {
          const canvas = canvasRef.current;
          const video = videoRef.current;
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0);
          const imageData = canvas.toDataURL('image/jpeg', 0.8);
          setSelfieImage(imageData);
          toast.success('Selfie captured successfully!');
          stopCamera();
        }
        setIsCapturing(false);
      }, 500);
    };
    
    // Liveness check removed - selfies will be manually reviewed

    const handleChangePassword = async () => {
      if (!currentPassword || !newPassword || !confirmPassword) {
        toast.error('All password fields are required');
        return;
      }
      if (newPassword !== confirmPassword) {
        toast.error('New passwords do not match');
        return;
      }
      if (newPassword.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      setSavingPassword(true);
      try {
        await axios.put(`${API}/api/user/change-password`, { 
          current_password: currentPassword, 
          new_password: newPassword 
        }, axiosConfig);
        toast.success('Password changed successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to change password');
      } finally {
        setSavingPassword(false);
      }
    };

    // Transaction PIN Management
    const handleSetPin = async () => {
      if (newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
        toast.error('PIN must be exactly 4 digits');
        return;
      }
      if (newPin !== confirmPin) {
        toast.error('PINs do not match');
        return;
      }
      setSavingPin(true);
      try {
        await axios.post(`${API}/api/user/pin/set`, { pin: newPin, confirm_pin: confirmPin }, axiosConfig);
        toast.success('Transaction PIN set successfully');
        setHasPin(true);
        setPinAction('');
        setNewPin('');
        setConfirmPin('');
        fetchProfile();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to set PIN');
      } finally {
        setSavingPin(false);
      }
    };

    const handleChangePin = async () => {
      if (newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
        toast.error('PIN must be exactly 4 digits');
        return;
      }
      if (newPin !== confirmPin) {
        toast.error('PINs do not match');
        return;
      }
      if (!currentPin) {
        toast.error('Current PIN is required');
        return;
      }
      setSavingPin(true);
      try {
        await axios.put(`${API}/api/user/pin/change`, { 
          current_pin: currentPin, 
          new_pin: newPin, 
          confirm_pin: confirmPin 
        }, axiosConfig);
        toast.success('Transaction PIN changed successfully');
        setPinAction('');
        setCurrentPin('');
        setNewPin('');
        setConfirmPin('');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to change PIN');
      } finally {
        setSavingPin(false);
      }
    };

    const handleRequestPinReset = async () => {
      setRequestingReset(true);
      try {
        await axios.post(`${API}/api/user/pin/reset-request`, {}, axiosConfig);
        toast.success('Reset code sent to your email');
        setPinAction('reset');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to send reset code');
      } finally {
        setRequestingReset(false);
      }
    };

    const handleResetPin = async () => {
      if (!resetCode || resetCode.length !== 6) {
        toast.error('Please enter the 6-digit reset code from your email');
        return;
      }
      if (newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
        toast.error('PIN must be exactly 4 digits');
        return;
      }
      if (newPin !== confirmPin) {
        toast.error('PINs do not match');
        return;
      }
      setSavingPin(true);
      try {
        await axios.post(`${API}/api/user/pin/reset-verify`, { 
          reset_code: resetCode, 
          bvn: resetBvn,
          new_pin: newPin, 
          confirm_pin: confirmPin 
        }, axiosConfig);
        toast.success('Transaction PIN reset successfully');
        setHasPin(true);
        setPinAction('');
        setResetCode('');
        setResetBvn('');
        setNewPin('');
        setConfirmPin('');
        fetchProfile();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to reset PIN');
      } finally {
        setSavingPin(false);
      }
    };

    // Tier 2 submission (just store BVN, no verification)
    const handleTier2Submit = async () => {
      if (bvn.length !== 11) {
        toast.error('BVN must be 11 digits');
        return;
      }
      
      try {
        const response = await axios.post(`${API}/api/kyc/tier2/submit`, {
          bvn: bvn,
          phone: kycPhone
        }, axiosConfig);
        
        if (response.data.success) {
          toast.success(`Tier 2 verified! Your limit is now ₦${(branding.kyc_tier2_max_balance || 500000).toLocaleString()}`);
          setUser({ ...user, tier: 2, bvn: bvn });
          fetchProfile();
        } else {
          toast.error(response.data.message || 'Tier 2 upgrade failed');
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Tier 2 submission failed');
      }
    };

    // Start Tier 3 Express KYC verification
    const startTier3Verification = async () => {
      if (bvn.length !== 11) {
        toast.error('Please enter a valid 11-digit BVN');
        return;
      }
      if (nin.length !== 11) {
        toast.error('Please enter a valid 11-digit NIN');
        return;
      }
      if (!kycPhone) {
        toast.error('Please enter your phone number');
        return;
      }
      if (!dob) {
        toast.error('Please enter your date of birth');
        return;
      }
      // Validate address fields for Payscribe
      if (!kycStreet.trim()) {
        toast.error('Please enter your street address');
        return;
      }
      if (!kycCity.trim()) {
        toast.error('Please enter your city');
        return;
      }
      if (!kycState.trim()) {
        toast.error('Please enter your state');
        return;
      }
      if (!selfieImage) {
        toast.error('Please take a selfie first');
        return;
      }
      if ((user.ngn_balance || 0) < KYC_FEE) {
        toast.error(`Insufficient balance. You need at least ₦${KYC_FEE} for Express KYC verification.`);
        return;
      }
      
      // Upload selfie first
      try {
        await axios.post(`${API}/api/user/upload-selfie`, {
          selfie_data: selfieImage
        }, axiosConfig);
      } catch (err) {
        toast.error('Failed to upload selfie. Please try again.');
        return;
      }
      
      setVerificationStep('bvn');
      setVerificationError('');
      setVerificationSuccess('');
      setShowVerificationPopup(true);
      verifyBVN();
    };

    // Verify BVN
    const verifyBVN = async () => {
      setVerifying(true);
      setVerificationError('');
      
      try {
        const response = await axios.post(`${API}/api/kyc/tier3/verify-bvn`, {
          bvn: bvn,
          nin: nin,
          phone: kycPhone,
          dob: dob
        }, axiosConfig);
        
        if (response.data.success) {
          setBvnVerified(true);
          setVerificationSuccess('BVN verified! Proceeding to NIN verification...');
          
          // Wait 2 seconds then start NIN verification
          setTimeout(() => {
            setVerificationStep('nin');
            setVerificationSuccess('');
            verifyNIN();
          }, 2000);
        }
      } catch (error) {
        setVerificationError(error.response?.data?.detail || 'BVN verification failed');
        // Initialize edit fields with current values for retry
        setEditFirstName(user.first_name || firstName);
        setEditLastName(user.last_name || lastName);
        setEditPhone(kycPhone);
        setEditDob(dob);
        setVerifying(false);
        fetchProfile(); // Refresh balance after fee deduction
      }
    };

    // Verify NIN
    const verifyNIN = async () => {
      setVerifying(true);
      setVerificationError('');
      
      try {
        const response = await axios.post(`${API}/api/kyc/tier3/verify-nin`, {
          bvn: bvn,
          nin: nin,
          phone: kycPhone,
          dob: dob,
          address: {
            street: kycStreet,
            city: kycCity,
            state: kycState,
            country: kycCountry || 'NG',
            postal_code: kycPostalCode || '100001'
          }
        }, axiosConfig);
        
        if (response.data.success) {
          setNinVerified(true);
          setVerificationSuccess('NIN verified! Your account is now Tier 3!');
          setUser({ ...user, tier: 3, bvn_verified: true, nin_verified: true });
          fetchProfile();
          
          // Close popup after 3 seconds
          setTimeout(() => {
            setShowVerificationPopup(false);
          }, 3000);
        }
      } catch (error) {
        setVerificationError(error.response?.data?.detail || 'NIN verification failed');
        // Initialize edit fields with current values for retry
        setEditFirstName(user.first_name || firstName);
        setEditLastName(user.last_name || lastName);
        setEditPhone(kycPhone);
        setEditDob(dob);
      } finally {
        setVerifying(false);
      }
    };

    // Handle retry verification with updated details
    const handleRetryVerification = async () => {
      // Validate balance for retry fee
      if ((user.ngn_balance || 0) < KYC_FEE) {
        toast.error(`Insufficient balance. You need ₦${KYC_FEE} for retry.`);
        return;
      }
      
      // Update user profile with edited values first
      try {
        await axios.put(`${API}/api/user/profile`, {
          first_name: editFirstName,
          last_name: editLastName,
          phone: editPhone
        }, axiosConfig);
        
        // Update local state
        setFirstName(editFirstName);
        setLastName(editLastName);
        setKycPhone(editPhone);
        setDob(editDob);
        
        // Clear error and retry
        setVerificationError('');
        setVerifying(true);
        
        if (verificationStep === 'bvn' || !bvnVerified) {
          // Retry BVN verification with updated details
          try {
            const response = await axios.post(`${API}/api/kyc/tier3/verify-bvn`, {
              bvn: bvn,
              nin: nin,
              phone: editPhone,
              dob: editDob
            }, axiosConfig);
            
            if (response.data.success) {
              setBvnVerified(true);
              setVerificationSuccess('BVN verified! Proceeding to NIN verification...');
              fetchProfile(); // Refresh balance
              
              setTimeout(() => {
                setVerificationStep('nin');
                setVerificationSuccess('');
                verifyNIN();
              }, 2000);
            }
          } catch (error) {
            setVerificationError(error.response?.data?.detail || 'BVN verification failed');
            fetchProfile(); // Refresh balance after fee deduction
          } finally {
            setVerifying(false);
          }
        } else {
          // Retry NIN verification
          try {
            const response = await axios.post(`${API}/api/kyc/tier3/verify-nin`, {
              bvn: bvn,
              nin: nin,
              phone: editPhone,
              dob: editDob,
              address: {
                street: kycStreet,
                city: kycCity,
                state: kycState,
                country: kycCountry || 'NG',
                postal_code: kycPostalCode || '100001'
              }
            }, axiosConfig);
            
            if (response.data.success) {
              setNinVerified(true);
              setVerificationSuccess('NIN verified! Your account is now Tier 3!');
              setUser({ ...user, tier: 3, bvn_verified: true, nin_verified: true });
              fetchProfile();
              
              setTimeout(() => {
                setShowVerificationPopup(false);
              }, 3000);
            }
          } catch (error) {
            setVerificationError(error.response?.data?.detail || 'NIN verification failed');
            fetchProfile(); // Refresh balance after fee deduction
          } finally {
            setVerifying(false);
          }
        }
      } catch (error) {
        toast.error('Failed to update profile. Please try again.');
        setVerifying(false);
      }
    };

    const getTierLimit = (tier) => {
      if (tier === 1) return `₦${(branding.kyc_tier1_max_balance || 50000).toLocaleString()}`;
      if (tier === 2) return `₦${(branding.kyc_tier2_max_balance || 500000).toLocaleString()}`;
      return `₦${(branding.kyc_tier3_max_balance || 2000000).toLocaleString()}`;
    };

    // Verification Popup Component
    const VerificationPopup = () => (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-card rounded-lg p-6 max-w-md w-full shadow-2xl animate-pulse-slow">
          <div className="text-center">
            <h3 className="text-xl font-bold text-foreground mb-2">Express KYC Verification</h3>
            <p className="text-sm text-muted-foreground mb-6">₦{KYC_FEE} total will be deducted for verification</p>
            
            {/* Progress Steps */}
            <div className="flex items-center justify-center gap-4 mb-8">
              {/* BVN Step */}
              <div className={`flex flex-col items-center ${verificationStep === 'bvn' ? 'scale-110' : ''} transition-transform`}>
                <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-lg ${
                  bvnVerified ? 'bg-primary' : verificationStep === 'bvn' ? 'bg-primary animate-pulse' : 'bg-muted'
                }`}>
                  {bvnVerified ? <Check className="w-7 h-7" /> : 'BVN'}
                </div>
                <span className="text-xs mt-2 font-medium text-muted-foreground">BVN</span>
                {verificationStep === 'bvn' && !bvnVerified && (
                  <RefreshCw className="w-4 h-4 mt-1 animate-spin text-primary" />
                )}
              </div>
              
              {/* Connector */}
              <div className={`w-12 h-1 rounded ${bvnVerified ? 'bg-primary' : 'bg-muted'}`} />
              
              {/* NIN Step */}
              <div className={`flex flex-col items-center ${verificationStep === 'nin' ? 'scale-110' : ''} transition-transform`}>
                <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-lg ${
                  ninVerified ? 'bg-primary' : verificationStep === 'nin' ? 'bg-primary animate-pulse' : 'bg-muted'
                }`}>
                  {ninVerified ? <Check className="w-7 h-7" /> : 'NIN'}
                </div>
                <span className="text-xs mt-2 font-medium text-muted-foreground">NIN</span>
                {verificationStep === 'nin' && !ninVerified && (
                  <RefreshCw className="w-4 h-4 mt-1 animate-spin text-primary" />
                )}
              </div>
            </div>
            
            {/* Status Message */}
            <div className="min-h-[60px]">
              {verifying && !verificationError && !verificationSuccess && (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-primary">
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    <span className="font-medium">Verifying {verificationStep.toUpperCase()}...</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {verificationStep === 'bvn' 
                      ? 'Matching phone, DOB & name with BVN records...'
                      : 'Matching DOB & name with NIN records...'
                    }
                  </p>
                </div>
              )}
              
              {verificationSuccess && (
                <div className="bg-primary/10 text-primary px-4 py-3 rounded-lg flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  <span className="font-medium">{verificationSuccess}</span>
                </div>
              )}
              
              {verificationError && (
                <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg text-left">
                  <p className="font-medium text-sm mb-3">{verificationError}</p>
                  
                  {/* Editable Fields for Retry */}
                  <div className="space-y-3 mt-4 p-3 bg-card rounded-md border border-destructive/30">
                    <p className="text-xs text-muted-foreground font-medium">Update your details and retry (₦{KYC_FEE} per attempt):</p>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-muted-foreground">First Name</label>
                        <input
                          type="text"
                          value={editFirstName}
                          onChange={(e) => setEditFirstName(e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border rounded focus:ring-1 focus:ring-primary text-foreground"
                          placeholder="First name"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Last Name</label>
                        <input
                          type="text"
                          value={editLastName}
                          onChange={(e) => setEditLastName(e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border rounded focus:ring-1 focus:ring-primary text-foreground"
                          placeholder="Last name"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-muted-foreground">Phone Number</label>
                        <input
                          type="tel"
                          value={editPhone}
                          onChange={(e) => setEditPhone(e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border rounded focus:ring-1 focus:ring-primary text-foreground"
                          placeholder="08012345678"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Date of Birth</label>
                        <input
                          type="date"
                          value={editDob}
                          onChange={(e) => setEditDob(e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border rounded focus:ring-1 focus:ring-primary text-foreground"
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-2 mt-3">
                      <button 
                        onClick={handleRetryVerification}
                        disabled={verifying}
                        className="flex-1 bg-primary text-white py-2 px-3 rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {verifying ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        Retry (₦{KYC_FEE})
                      </button>
                      <button 
                        onClick={() => setShowVerificationPopup(false)}
                        className="px-4 py-2 border border-border rounded-md text-sm text-muted-foreground hover:bg-muted/50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Tier 3 Success */}
            {ninVerified && (
              <div className="mt-4 p-4 bg-gradient-to-r from-emerald-500 to-primary rounded-lg text-white">
                <h4 className="font-bold text-lg">🎉 Congratulations!</h4>
                <p className="text-sm opacity-90">Your account is now Tier 3 with ₦{(branding.kyc_tier3_max_balance || 2000000).toLocaleString()} limit</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );

    return (
      <div className="space-y-6">
        {showVerificationPopup && <VerificationPopup />}
        
        <p className="text-xs text-muted-foreground">Manage your account</p><h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-foreground">Profile settings</h1>
        
        {/* Profile/KYC Tabs */}
        <div className="flex gap-2 border-b border-border pb-2">
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
              activeTab === 'profile' 
                ? 'bg-primary/10 text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-muted-foreground'
            }`}
          >
            Profile Info
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors flex items-center gap-2 ${
              activeTab === 'security' 
                ? 'bg-primary/10 text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-muted-foreground'
            }`}
          >
            Security & PIN
            {!hasPin && <span className="px-2 py-0.5 bg-destructive/10 text-destructive text-xs rounded-full">Set PIN</span>}
          </button>
          <button
            onClick={() => setActiveTab('kyc')}
            className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors flex items-center gap-2 ${
              activeTab === 'kyc' 
                ? 'bg-primary/10 text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-muted-foreground'
            }`}
          >
            Account Upgrade (KYC)
            {userTier < 3 && <span className="px-2 py-0.5 bg-amber-50 text-amber-600 text-xs rounded-full">Upgrade</span>}
          </button>
        </div>

        {activeTab === 'profile' && (
          <>
            {/* Profile Info Card */}
            <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
              <h3 className="text-lg font-semibold text-foreground mb-4">Account Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    First Name
                    {userTier >= 2 && <span className="ml-2 text-xs text-muted-foreground">(Locked after KYC)</span>}
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={userTier >= 2}
                    className={`w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${userTier >= 2 ? 'bg-muted/50 text-muted-foreground cursor-not-allowed' : ''}`}
                    style={{ '--tw-ring-color': primaryColor }}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Last Name
                    {userTier >= 2 && <span className="ml-2 text-xs text-muted-foreground">(Locked after KYC)</span>}
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={userTier >= 2}
                    className={`w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${userTier >= 2 ? 'bg-muted/50 text-muted-foreground cursor-not-allowed' : ''}`}
                    style={{ '--tw-ring-color': primaryColor }}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Email</label>
                  <input
                    type="email"
                    value={user.email}
                    disabled
                    className="w-full px-4 py-3 border border-border rounded-lg bg-muted/50 text-muted-foreground"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Phone Number</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Address</label>
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
                    placeholder="Enter your address"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Account Tier</label>
                  <div className="px-4 py-3 rounded-lg bg-muted/50 border border-border">
                    <span className="font-semibold" style={{ color: primaryColor }}>Tier {userTier}</span>
                    <span className="text-muted-foreground text-sm ml-2">(Limit: {getTierLimit(userTier)})</span>
                  </div>
                </div>
                {user.payscribe_customer_id && (
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">PCID</label>
                    <div className="px-4 py-3 rounded-lg bg-muted/50 border border-border flex items-center justify-between">
                      <span className="text-muted-foreground font-mono text-sm">{user.payscribe_customer_id}</span>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(user.payscribe_customer_id);
                          toast.success('PCID copied!');
                        }}
                        className="p-1.5 hover:bg-muted rounded-md transition-colors"
                        title="Copy PCID"
                      >
                        <Copy className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Payment Card ID for virtual card services</p>
                  </div>
                )}
              </div>

              {/* Verification Address - Read Only (only shown if user has verification address) */}
              {user.kyc_address && (
                <div className="mt-6 pt-6 border-t border-border">
                  <h4 className="text-md font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-primary" />
                    Verification Address
                    <span className="text-xs text-muted-foreground font-normal">(Read-only)</span>
                  </h4>
                  <div className="bg-muted/50 rounded-lg p-4 border border-border">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Street Address</p>
                        <p className="text-sm font-medium text-foreground">{user.kyc_address.street || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">City</p>
                        <p className="text-sm font-medium text-foreground">{user.kyc_address.city || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">State</p>
                        <p className="text-sm font-medium text-foreground">{user.kyc_address.state || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Postal Code</p>
                        <p className="text-sm font-medium text-foreground">{user.kyc_address.postal_code || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Country</p>
                        <p className="text-sm font-medium text-foreground">{user.kyc_address.country || 'Nigeria'}</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3">This address was provided during KYC verification and cannot be edited. Contact support if you need to update it.</p>
                  </div>
                </div>
              )}
              
              <button
                onClick={handleUpdateProfile}
                disabled={savingProfile}
                className="mt-4 px-6 py-3 text-white rounded-lg font-semibold transition-colors disabled:bg-muted"
                style={{ backgroundColor: primaryColor }}
              >
                {savingProfile ? 'Saving...' : 'Save Profile'}
              </button>
            </div>

            {/* Balance Card */}
            <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
              <h3 className="text-lg font-semibold text-foreground mb-4">Wallet Balances</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg" style={{ backgroundColor: 'hsl(var(--primary) / 0.06)' }}>
                  <p className="text-sm text-muted-foreground">NGN Balance</p>
                  <p className="text-3xl font-bold" style={{ color: primaryColor }}>₦{(user.ngn_balance || 0).toLocaleString()}</p>
                </div>
                <div className="p-4 bg-primary/10 rounded-lg">
                  <p className="text-sm text-muted-foreground">USD Balance</p>
                  <p className="text-3xl font-bold text-primary">${(user.usd_balance || 0).toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* Change Password Card */}
            <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
              <h3 className="text-lg font-semibold text-foreground mb-4">Change Password</h3>
              <div className="space-y-4 max-w-md">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Current Password</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
                    placeholder="Enter current password"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
                    placeholder="Enter new password"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
                    placeholder="Confirm new password"
                  />
                </div>
                <button
                  onClick={handleChangePassword}
                  disabled={savingPassword}
                  className="px-6 py-3 text-white rounded-lg font-semibold transition-colors disabled:bg-muted"
                  style={{ backgroundColor: primaryColor }}
                >
                  {savingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Security & PIN Tab */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            {/* Transaction PIN Section */}
            <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
              <h3 className="text-lg font-semibold text-foreground mb-2">Transaction PIN</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Your 4-digit PIN is required to confirm all bill payments and bank transfers.
              </p>
              
              {!hasPin && !pinAction && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-4">
                  <p className="text-sm text-destructive">
                    <strong>PIN not set!</strong> You must set a transaction PIN before making any transfers or bill payments.
                  </p>
                </div>
              )}

              {!pinAction && (
                <div className="flex flex-wrap gap-3">
                  {!hasPin ? (
                    <button
                      onClick={() => setPinAction('set')}
                      className="px-4 py-2 text-white rounded-md font-medium"
                      style={{ backgroundColor: primaryColor }}
                    >
                      Set Transaction PIN
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => setPinAction('change')}
                        className="px-4 py-2 bg-muted text-muted-foreground rounded-md font-medium hover:bg-muted"
                      >
                        Change PIN
                      </button>
                      <button
                        onClick={handleRequestPinReset}
                        disabled={requestingReset}
                        className="px-4 py-2 bg-amber-50 text-amber-600 rounded-md font-medium hover:bg-amber-50"
                      >
                        {requestingReset ? 'Sending...' : 'Forgot PIN?'}
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Set PIN Form */}
              {pinAction === 'set' && (
                <div className="space-y-4 mt-4 p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-medium text-foreground">Create Transaction PIN</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">New PIN</label>
                      <input
                        type="password"
                        maxLength={4}
                        value={newPin}
                        onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        placeholder="••••"
                        className="w-full px-4 py-3 border border-border rounded-lg text-center text-xl tracking-widest"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Confirm PIN</label>
                      <input
                        type="password"
                        maxLength={4}
                        value={confirmPin}
                        onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        placeholder="••••"
                        className="w-full px-4 py-3 border border-border rounded-lg text-center text-xl tracking-widest"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSetPin}
                      disabled={savingPin}
                      className="px-4 py-2 text-white rounded-md font-medium"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {savingPin ? 'Setting...' : 'Set PIN'}
                    </button>
                    <button
                      onClick={() => { setPinAction(''); setNewPin(''); setConfirmPin(''); }}
                      className="px-4 py-2 bg-muted text-muted-foreground rounded-md font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Change PIN Form */}
              {pinAction === 'change' && (
                <div className="space-y-4 mt-4 p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-medium text-foreground">Change Transaction PIN</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Current PIN</label>
                      <input
                        type="password"
                        maxLength={4}
                        value={currentPin}
                        onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        placeholder="••••"
                        className="w-full px-4 py-3 border border-border rounded-lg text-center text-xl tracking-widest"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">New PIN</label>
                      <input
                        type="password"
                        maxLength={4}
                        value={newPin}
                        onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        placeholder="••••"
                        className="w-full px-4 py-3 border border-border rounded-lg text-center text-xl tracking-widest"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Confirm PIN</label>
                      <input
                        type="password"
                        maxLength={4}
                        value={confirmPin}
                        onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        placeholder="••••"
                        className="w-full px-4 py-3 border border-border rounded-lg text-center text-xl tracking-widest"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleChangePin}
                      disabled={savingPin}
                      className="px-4 py-2 text-white rounded-md font-medium"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {savingPin ? 'Changing...' : 'Change PIN'}
                    </button>
                    <button
                      onClick={() => { setPinAction(''); setCurrentPin(''); setNewPin(''); setConfirmPin(''); }}
                      className="px-4 py-2 bg-muted text-muted-foreground rounded-md font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Reset PIN Form */}
              {pinAction === 'reset' && (
                <div className="space-y-4 mt-4 p-4 bg-amber-50 rounded-lg border border-amber-300">
                  <h4 className="font-medium text-amber-600">Reset Transaction PIN</h4>
                  <p className="text-sm text-amber-600">
                    A reset code has been sent to your email. Enter the code and your BVN to reset your PIN.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Reset Code (from email)</label>
                      <input
                        type="text"
                        maxLength={6}
                        value={resetCode}
                        onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="123456"
                        className="w-full px-4 py-3 border border-border rounded-lg text-center text-xl tracking-widest"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">BVN (for verification)</label>
                      <input
                        type="text"
                        maxLength={11}
                        value={resetBvn}
                        onChange={(e) => setResetBvn(e.target.value.replace(/\D/g, '').slice(0, 11))}
                        placeholder="Enter your BVN"
                        className="w-full px-4 py-3 border border-border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">New PIN</label>
                      <input
                        type="password"
                        maxLength={4}
                        value={newPin}
                        onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        placeholder="••••"
                        className="w-full px-4 py-3 border border-border rounded-lg text-center text-xl tracking-widest"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Confirm PIN</label>
                      <input
                        type="password"
                        maxLength={4}
                        value={confirmPin}
                        onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        placeholder="••••"
                        className="w-full px-4 py-3 border border-border rounded-lg text-center text-xl tracking-widest"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleResetPin}
                      disabled={savingPin}
                      className="px-4 py-2 text-white rounded-md font-medium"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {savingPin ? 'Resetting...' : 'Reset PIN'}
                    </button>
                    <button
                      onClick={() => { setPinAction(''); setResetCode(''); setResetBvn(''); setNewPin(''); setConfirmPin(''); }}
                      className="px-4 py-2 bg-muted text-muted-foreground rounded-md font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {hasPin && !pinAction && (
                <div className="mt-4 p-3 bg-primary/10 border border-primary rounded-lg flex items-center gap-2">
                  <Check className="w-5 h-5 text-primary" />
                  <span className="text-sm text-primary">Transaction PIN is active</span>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'kyc' && (
          <div className="space-y-6">
            {/* Tier Progress */}
            <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
              <h3 className="text-lg font-semibold text-foreground mb-4">Account Tier Status</h3>
              <div className="flex items-center gap-4 mb-4">
                {[
                  { tier: 1, limit: `₦${(branding.kyc_tier1_max_balance || 50000).toLocaleString()}`, desc: 'Basic' },
                  { tier: 2, limit: `₦${(branding.kyc_tier2_max_balance || 500000).toLocaleString()}`, desc: 'BVN' },
                  { tier: 3, limit: `₦${(branding.kyc_tier3_max_balance || 2000000).toLocaleString()}`, desc: 'Express KYC' }
                ].map((t) => (
                  <div key={t.tier} className={`flex-1 text-center p-3 rounded-lg transition-all ${
                    userTier >= t.tier 
                      ? 'bg-primary/10 border-2 border-primary' 
                      : 'bg-muted border-2 border-border'
                  }`}>
                    <p className={`font-bold text-lg ${userTier >= t.tier ? 'text-primary' : 'text-muted-foreground'}`}>
                      Tier {t.tier}
                    </p>
                    <p className="text-xs text-muted-foreground">{t.limit}</p>
                    <p className="text-[10px] text-muted-foreground">{t.desc}</p>
                    {userTier === t.tier && <span className="text-xs text-primary font-medium">Current</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* Tier 1 → Tier 2 Upgrade */}
            {userTier === 1 && (
              <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
                <h3 className="text-lg font-semibold text-foreground mb-2">Upgrade to Tier 2</h3>
                <p className="text-sm text-muted-foreground mb-4">Increase your limit to ₦{(branding.kyc_tier2_max_balance || 500000).toLocaleString()} by providing your BVN.</p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">BVN (11 digits)</label>
                    <input
                      type="text"
                      value={bvn}
                      onChange={(e) => setBvn(e.target.value.replace(/\D/g, '').slice(0, 11))}
                      className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Enter your BVN"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Phone Number</label>
                    <input
                      type="tel"
                      value={kycPhone}
                      onChange={(e) => setKycPhone(e.target.value)}
                      className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="08012345678"
                    />
                  </div>
                  <button
                    onClick={handleTier2Submit}
                    disabled={bvn.length !== 11}
                    className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground font-bold text-sm shadow-cta hover:brightness-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Upgrade to Tier 2
                  </button>
                </div>
              </div>
            )}

            {/* Tier 2 → Tier 3 Express KYC */}
            {userTier === 2 && (
              <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">Express KYC Verification</h3>
                    <p className="text-sm text-muted-foreground">Upgrade to Tier 3 with ₦{(branding.kyc_tier3_max_balance || 2000000).toLocaleString()} limit</p>
                  </div>
                  <div className="px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: 'hsl(var(--accent) / 0.12)', color: accentColor }}>
                    ₦{KYC_FEE} total fee
                  </div>
                </div>
                
                <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-primary mb-2">What we verify:</h4>
                  <ul className="text-sm text-primary space-y-1">
                    <li>• <strong>BVN:</strong> Phone number, Date of Birth, Names</li>
                    <li>• <strong>NIN:</strong> Date of Birth, Names</li>
                  </ul>
                  <p className="text-xs text-primary mt-2">Verification is instant - results in seconds!</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">BVN (11 digits) *</label>
                    <input
                      type="text"
                      value={bvn}
                      onChange={(e) => setBvn(e.target.value.replace(/\D/g, '').slice(0, 11))}
                      className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2"
                      style={{ '--tw-ring-color': primaryColor }}
                      placeholder="Enter your BVN"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">NIN (11 digits) *</label>
                    <input
                      type="text"
                      value={nin}
                      onChange={(e) => setNin(e.target.value.replace(/\D/g, '').slice(0, 11))}
                      className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2"
                      style={{ '--tw-ring-color': primaryColor }}
                      placeholder="Enter your NIN"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Phone Number *</label>
                    <input
                      type="tel"
                      value={kycPhone}
                      onChange={(e) => setKycPhone(e.target.value)}
                      className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2"
                      style={{ '--tw-ring-color': primaryColor }}
                      placeholder="Must match BVN phone"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Date of Birth *</label>
                    <input
                      type="date"
                      value={dob}
                      onChange={(e) => setDob(e.target.value)}
                      className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2"
                      style={{ '--tw-ring-color': primaryColor }}
                    />
                  </div>
                </div>

                {/* Address Section for Payscribe */}
                <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-semibold text-primary mb-3">Address Information (Required for Card Services)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Street Address *</label>
                      <input
                        type="text"
                        value={kycStreet}
                        onChange={(e) => setKycStreet(e.target.value)}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 bg-card"
                        style={{ '--tw-ring-color': primaryColor }}
                        placeholder="e.g., 123 Main Street, Lekki Phase 1"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">City *</label>
                      <input
                        type="text"
                        value={kycCity}
                        onChange={(e) => setKycCity(e.target.value)}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 bg-card"
                        style={{ '--tw-ring-color': primaryColor }}
                        placeholder="e.g., Lagos"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">State *</label>
                      <select
                        value={kycState}
                        onChange={(e) => setKycState(e.target.value)}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 bg-card"
                        style={{ '--tw-ring-color': primaryColor }}
                      >
                        <option value="">Select State</option>
                        <option value="Abia">Abia</option>
                        <option value="Adamawa">Adamawa</option>
                        <option value="Akwa Ibom">Akwa Ibom</option>
                        <option value="Anambra">Anambra</option>
                        <option value="Bauchi">Bauchi</option>
                        <option value="Bayelsa">Bayelsa</option>
                        <option value="Benue">Benue</option>
                        <option value="Borno">Borno</option>
                        <option value="Cross River">Cross River</option>
                        <option value="Delta">Delta</option>
                        <option value="Ebonyi">Ebonyi</option>
                        <option value="Edo">Edo</option>
                        <option value="Ekiti">Ekiti</option>
                        <option value="Enugu">Enugu</option>
                        <option value="FCT">FCT (Abuja)</option>
                        <option value="Gombe">Gombe</option>
                        <option value="Imo">Imo</option>
                        <option value="Jigawa">Jigawa</option>
                        <option value="Kaduna">Kaduna</option>
                        <option value="Kano">Kano</option>
                        <option value="Katsina">Katsina</option>
                        <option value="Kebbi">Kebbi</option>
                        <option value="Kogi">Kogi</option>
                        <option value="Kwara">Kwara</option>
                        <option value="Lagos">Lagos</option>
                        <option value="Nasarawa">Nasarawa</option>
                        <option value="Niger">Niger</option>
                        <option value="Ogun">Ogun</option>
                        <option value="Ondo">Ondo</option>
                        <option value="Osun">Osun</option>
                        <option value="Oyo">Oyo</option>
                        <option value="Plateau">Plateau</option>
                        <option value="Rivers">Rivers</option>
                        <option value="Sokoto">Sokoto</option>
                        <option value="Taraba">Taraba</option>
                        <option value="Yobe">Yobe</option>
                        <option value="Zamfara">Zamfara</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Postal Code</label>
                      <input
                        type="text"
                        value={kycPostalCode}
                        onChange={(e) => setKycPostalCode(e.target.value)}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 bg-card"
                        style={{ '--tw-ring-color': primaryColor }}
                        placeholder="e.g., 100001"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Country</label>
                      <select
                        value={kycCountry}
                        onChange={(e) => setKycCountry(e.target.value)}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 bg-card"
                        style={{ '--tw-ring-color': primaryColor }}
                      >
                        <option value="NG">Nigeria</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="bg-muted/50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-muted-foreground">
                    <strong>Important:</strong> Your registered name ({user.first_name} {user.last_name}) must match your BVN/NIN records.
                  </p>
                </div>

                {/* Selfie Verification Section */}
                <div className="bg-card border border-border rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-foreground">Selfie Verification</h4>
                      <p className="text-xs text-muted-foreground">Take a selfie to prove you're a real person</p>
                    </div>
                    {selfieImage && <Check className="w-5 h-5 text-primary" />}
                  </div>
                  
                  {!selfieImage ? (
                    <button
                      onClick={startCamera}
                      className="w-full py-3 border-2 border-dashed border-border rounded-lg text-muted-foreground hover:border-primary/40 hover:bg-muted/50 transition-colors flex items-center justify-center gap-2"
                    >
                      <Camera className="w-5 h-5" />
                      Open Camera to Take Selfie
                    </button>
                  ) : (
                    <div className="flex items-center gap-3">
                      <img src={selfieImage} alt="Selfie" className="w-16 h-16 rounded-md object-cover" />
                      <div className="flex-1">
                        <p className="text-sm text-primary font-medium">Selfie captured!</p>
                        <button 
                          onClick={() => setSelfieImage(null)} 
                          className="text-xs text-destructive hover:underline"
                        >
                          Retake
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {cameraError && (
                    <p className="text-sm text-destructive mt-2">{cameraError}</p>
                  )}
                </div>
                
                <button
                  onClick={startTier3Verification}
                  disabled={bvn.length !== 11 || nin.length !== 11 || !kycPhone || !dob || !kycStreet.trim() || !kycCity.trim() || !kycState.trim() || !selfieImage}
                  className="w-full py-4 text-white rounded-lg font-bold transition-all shadow-lg disabled:bg-muted disabled:shadow-none"
                  style={{ backgroundColor: bvn.length === 11 && nin.length === 11 && kycPhone && dob && kycStreet.trim() && kycCity.trim() && kycState.trim() && selfieImage ? primaryColor : undefined }}
                >
                  Start Express KYC (₦{KYC_FEE})
                </button>

                {/* Camera Modal - Simple selfie capture */}
                {showCamera && (
                  <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-lg max-w-md w-full overflow-hidden">
                      <div className="p-4 border-b border-border">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-foreground">Take a Selfie</h3>
                          <button onClick={stopCamera} className="text-muted-foreground hover:text-muted-foreground">
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">Position your face in the frame and capture</p>
                      </div>
                      
                      <div className="relative aspect-[4/3] bg-black">
                        <video 
                          ref={videoRef} 
                          autoPlay 
                          playsInline 
                          muted
                          className="w-full h-full object-cover"
                        />
                        <canvas ref={canvasRef} className="hidden" />
                        
                        {/* Face Guide */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="w-48 h-56 border-4 border-white/50 rounded-full" />
                        </div>
                        
                        {/* Instructions */}
                        <div className="absolute top-4 left-0 right-0 text-center">
                          <div className="inline-block bg-black/60 text-white px-4 py-2 rounded-full text-sm">
                            📸 Position your face and tap Capture
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-4 space-y-3">
                        <button
                          onClick={captureSelfie}
                          disabled={isCapturing}
                          className="w-full py-3 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
                          style={{ backgroundColor: primaryColor }}
                        >
                          {isCapturing ? 'Capturing...' : '📸 Capture Selfie'}
                        </button>
                        
                        <p className="text-xs text-center text-muted-foreground">
                          Position your face within the oval for best results
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tier 3 Complete */}
            {userTier >= 3 && (
              <div className="bg-card p-6 rounded-lg border border-border shadow-sm text-center">
                <div className="w-20 h-20 mx-auto bg-gradient-to-r from-emerald-500 to-primary rounded-full flex items-center justify-center mb-4">
                  <Check className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">Fully Verified!</h3>
                <p className="text-muted-foreground mb-4">Your account is at maximum tier with ₦{(branding.kyc_tier3_max_balance || 2000000).toLocaleString()} limit.</p>
                <div className="flex justify-center gap-4">
                  <div className="bg-primary/10 px-4 py-2 rounded-md">
                    <span className="text-primary font-medium">BVN ✓</span>
                  </div>
                  <div className="bg-primary/10 px-4 py-2 rounded-md">
                    <span className="text-primary font-medium">NIN ✓</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  function ReferralSection() {
    const primaryColor = branding.primary_color_hex || '#059669';
    const referralCode = user.referral_code || user.email?.split('@')[0]?.toUpperCase() || 'NOCODE';
    const referralLink = `${window.location.origin}?ref=${referralCode}`;
    
    const copyReferralLink = () => {
      navigator.clipboard.writeText(referralLink);
      toast.success('Referral link copied!');
    };

    const copyReferralCode = () => {
      navigator.clipboard.writeText(referralCode);
      toast.success('Referral code copied!');
    };

    return (
      <div className="space-y-6">
        <p className="text-xs text-muted-foreground">Invite friends and earn</p><h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-foreground">Referral program</h1>
        
        {/* Referral Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-2xl bg-card shadow-card p-5 text-center">
            <p className="text-3xl font-bold" style={{ color: primaryColor }}>{user.referral_count || 0}</p>
            <p className="text-sm text-muted-foreground mt-1">Total Referrals</p>
          </div>
          <div className="rounded-2xl bg-card shadow-card p-5 text-center">
            <p className="text-3xl font-bold text-amber-600">₦{(user.referral_earnings || 0).toLocaleString()}</p>
            <p className="text-sm text-muted-foreground mt-1">Total Earnings</p>
          </div>
          <div className="rounded-2xl bg-card shadow-card p-5 text-center">
            <p className="text-3xl font-bold text-primary">5%</p>
            <p className="text-sm text-muted-foreground mt-1">Commission Rate</p>
          </div>
        </div>
        
        {/* Referral Code Card */}
        <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
          <h3 className="text-lg font-semibold text-foreground mb-4">Your Referral Code</h3>
          
          <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: 'hsl(var(--primary) / 0.06)' }}>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold tracking-wider" style={{ color: primaryColor }}>{referralCode}</span>
              <button 
                onClick={copyReferralCode}
                className="px-4 py-2 text-white rounded-md text-sm font-semibold"
                style={{ backgroundColor: primaryColor }}
              >
                Copy Code
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-muted-foreground">Referral Link</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={referralLink}
                readOnly
                className="flex-1 px-4 py-3 border border-border rounded-lg bg-muted/50 text-sm"
              />
              <button 
                onClick={copyReferralLink}
                className="px-4 py-3 text-white rounded-lg text-sm font-semibold"
                style={{ backgroundColor: primaryColor }}
              >
                Copy Link
              </button>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
          <h3 className="text-lg font-semibold text-foreground mb-4">How It Works</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { step: '1', title: 'Share Your Link', desc: 'Share your unique referral link with friends' },
              { step: '2', title: 'They Sign Up', desc: 'When they register using your link' },
              { step: '3', title: 'Earn Rewards', desc: 'Get 5% of their first deposit as bonus!' }
            ].map((item, idx) => (
              <div key={idx} className="text-center p-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 text-white font-bold" style={{ backgroundColor: primaryColor }}>
                  {item.step}
                </div>
                <h4 className="font-semibold text-foreground">{item.title}</h4>
                <p className="text-sm text-muted-foreground mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  function AccountUpgradeSection() {
    const [documentType, setDocumentType] = useState('');
    const [documentNumber, setDocumentNumber] = useState('');
    const [bvn, setBvn] = useState('');
    const [idDocument, setIdDocument] = useState(null);
    const [selfie, setSelfie] = useState(null);
    const [street, setStreet] = useState('');
    const [apartment, setApartment] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [country, setCountry] = useState('NG');
    const [dob, setDob] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const userTier = user.tier || 1;
    const walletBalance = user.ngn_balance + (user.usd_balance * 1500);

    const getTierLimit = (tier) => {
      if (tier === 1) return branding.kyc_tier1_max_balance || 50000;
      if (tier === 2) return branding.kyc_tier2_max_balance || 500000;
      return branding.kyc_tier3_max_balance || 2000000;
    };

    const handleSubmitKYC = async () => {
      if (!documentType || !documentNumber || !bvn || !idDocument || !selfie || !street || !city || !state || !postalCode || !dob) {
        toast.error('Please fill all required fields including BVN');
        return;
      }

      if (bvn.length !== 11) {
        toast.error('BVN must be 11 digits');
        return;
      }

      setSubmitting(true);
      try {
        const formData = new FormData();
        formData.append('idDocument', idDocument);
        formData.append('selfie', selfie);

        // Upload files first
        const uploadRes = await axios.post(`${API}/api/user/upload-kyc-documents`, formData, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            'Content-Type': 'multipart/form-data'
          }
        });

        // Create Payscribe customer
        const response = await axios.post(
          `${API}/api/payscribe/create-customer`,
          {
            first_name: user.full_name?.split(' ')[0] || 'User',
            last_name: user.full_name?.split(' ').slice(1).join(' ') || 'Name',
            phone: user.phone,
            email: user.email,
            dob: dob,
            country: country,
            address: {
              street: street + (apartment ? `, ${apartment}` : ''),
              city: city,
              state: state,
              country: country,
              postal_code: postalCode
            },
            identification_type: 'BVN',
            identification_number: bvn,
            photo: uploadRes.data.selfie_url,
            identity: {
              type: documentType,
              number: documentNumber,
              country: country,
              image: uploadRes.data.id_document_url
            }
          },
          axiosConfig
        );

        if (response.data.success) {
          toast.success('KYC submitted successfully! Review in 1-2 business days.');
          fetchProfile();
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'KYC submission failed');
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-primary text-white rounded-lg p-6">
          <p className="text-xs text-muted-foreground">Raise your transaction limits</p><h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-foreground">Account upgrade</h1>
          <p className="text-white/90">Unlock higher limits and premium features</p>
        </div>

        {/* Tier Status */}
        <div className="bg-card rounded-lg p-6 border shadow-sm">
          <h2 className="text-xl font-bold text-foreground mb-4">Current Tier: Tier {userTier}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Tier 1 */}
            <div className={`p-4 rounded-lg border-2 ${userTier >= 1 ? 'border-primary bg-primary/10' : 'border-border'}`}>
              <div className="flex items-center gap-2 mb-2">
                {userTier >= 1 && <Check className="w-5 h-5 text-primary" />}
                <h3 className="font-bold">Tier 1: Basic</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-2">Email verification</p>
              <p className="text-lg font-bold text-primary">₦{(branding.kyc_tier1_max_balance || 50000).toLocaleString()} limit</p>
              {userTier >= 1 && <p className="text-xs text-primary mt-2">✓ Approved</p>}
            </div>

            {/* Tier 2 */}
            <div className={`p-4 rounded-lg border-2 ${userTier >= 2 ? 'border-primary bg-primary/10' : 'border-border'}`}>
              <div className="flex items-center gap-2 mb-2">
                {userTier >= 2 && <Check className="w-5 h-5 text-primary" />}
                <h3 className="font-bold">Tier 2: Standard</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-2">BVN verification</p>
              <p className="text-lg font-bold text-primary">₦{(branding.kyc_tier2_max_balance || 500000).toLocaleString()} limit</p>
              {userTier >= 2 && <p className="text-xs text-primary mt-2">✓ Approved</p>}
            </div>

            {/* Tier 3 */}
            <div className={`p-4 rounded-lg border-2 ${userTier >= 3 ? 'border-primary bg-primary/10' : 'border-amber-300 bg-amber-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                {userTier >= 3 ? <Check className="w-5 h-5 text-primary" /> : <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" /></svg>}
                <h3 className="font-bold">Tier 3: Premium</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-2">Full KYC verification</p>
              <p className="text-lg font-bold text-primary">₦{(branding.kyc_tier3_max_balance || 2000000).toLocaleString()} limit</p>
              {userTier < 3 && <p className="text-xs text-amber-600 mt-2">⚠ Upgrade Required</p>}
            </div>
          </div>

          {/* Warning */}
          {walletBalance >= getTierLimit(userTier) * 0.8 && userTier < 3 && (
            <div className="bg-destructive/10 border-2 border-destructive/30 rounded-lg p-4">
              <p className="text-sm text-destructive">
                <strong>⚠️ Important: Wallet Threshold Notice</strong><br/>
                Your account will be automatically locked if your wallet balance reaches ₦{getTierLimit(userTier).toLocaleString()}. 
                Please upgrade to Tier 3 to avoid service interruption.
              </p>
            </div>
          )}
        </div>

        {/* Upgrade Form */}
        {userTier < 3 && (
          <div className="bg-card rounded-lg p-6 border shadow-sm">
            <h2 className="text-xl font-bold text-foreground mb-4">Upgrade to Tier 3</h2>
            <p className="text-muted-foreground mb-6">
              Complete full KYC verification to unlock:
            </p>
            <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-1">
              <li>₦1,000,000 wallet limit</li>
              <li>USDT/USDC stablecoin deposits</li>
              <li>Virtual card creation</li>
              <li>Priority support</li>
            </ul>

            <div className="space-y-4">
              {/* Document Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Document Type</label>
                  <select
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-border rounded-md focus:border-primary focus:outline-none text-foreground"
                  >
                    <option value="">-- Select Document Type --</option>
                    <option value="NIN">National ID (NIN)</option>
                    <option value="PASSPORT">International Passport</option>
                    <option value="DRIVERS_LICENSE">Driver&apos;s License</option>
                    <option value="VOTERS_CARD">Voter&apos;s Card</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Document Number</label>
                  <input
                    type="text"
                    placeholder="Enter document number"
                    value={documentNumber}
                    onChange={(e) => setDocumentNumber(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-border rounded-md focus:border-primary focus:outline-none text-foreground"
                  />
                </div>
              </div>

              {/* BVN - REQUIRED */}
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Bank Verification Number (BVN) <span className="text-destructive">*Required</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter your 11-digit BVN"
                  value={bvn}
                  onChange={(e) => setBvn(e.target.value.replace(/\D/g, '').slice(0, 11))}
                  maxLength={11}
                  className="w-full px-4 py-3 border-2 border-border rounded-md focus:border-primary focus:outline-none text-foreground"
                />
                <p className="text-xs text-muted-foreground mt-1">Required for Payscribe customer creation</p>
              </div>

              {/* Date of Birth */}
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Date of Birth</label>
                <input
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-border rounded-md focus:border-primary focus:outline-none text-foreground"
                />
              </div>

              {/* Document Upload */}
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Upload ID Document</label>
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={(e) => setIdDocument(e.target.files[0])}
                  className="w-full px-4 py-3 border-2 border-border rounded-md focus:border-primary focus:outline-none text-foreground"
                />
                <p className="text-xs text-muted-foreground mt-1">JPEG, PNG only. Max 4MB</p>
              </div>

              {/* Selfie Upload */}
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Upload Selfie</label>
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={(e) => setSelfie(e.target.files[0])}
                  className="w-full px-4 py-3 border-2 border-border rounded-md focus:border-primary focus:outline-none text-foreground"
                />
                <p className="text-xs text-muted-foreground mt-1">Clear photo of your face. JPEG, PNG only. Max 4MB</p>
              </div>

              {/* Address */}
              <h3 className="text-lg font-bold text-foreground mt-6">Address Information</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Street Address</label>
                  <input
                    type="text"
                    placeholder="e.g. 56, Adeola Odeku, Victoria Island"
                    value={street}
                    onChange={(e) => setStreet(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-border rounded-md focus:border-primary focus:outline-none text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Apartment / Suite (Optional)</label>
                  <input
                    type="text"
                    placeholder="Apt 4B"
                    value={apartment}
                    onChange={(e) => setApartment(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-border rounded-md focus:border-primary focus:outline-none text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">City</label>
                  <input
                    type="text"
                    placeholder="Lagos"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-border rounded-md focus:border-primary focus:outline-none text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">State</label>
                  <input
                    type="text"
                    placeholder="Lagos State"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-border rounded-md focus:border-primary focus:outline-none text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Postal Code</label>
                  <input
                    type="text"
                    placeholder="100001"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-border rounded-md focus:border-primary focus:outline-none text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Country</label>
                  <input
                    type="text"
                    value={country}
                    disabled
                    className="w-full px-4 py-3 border-2 border-border rounded-md bg-muted text-muted-foreground"
                  />
                </div>
              </div>

              <button
                onClick={handleSubmitKYC}
                disabled={submitting}
                className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground font-bold text-sm shadow-cta hover:brightness-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Submitting...' : 'Submit Documents for Review'}
              </button>

              <p className="text-xs text-muted-foreground mt-3 text-center">
                Your documents will be securely reviewed by our team within 1-2 business days.
              </p>
            </div>
          </div>
        )}

        {/* Already Tier 3 */}
        {userTier >= 3 && (
          <div className="bg-primary/10 border-2 border-primary rounded-lg p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-primary rounded-full flex items-center justify-center">
              <Check className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-foreground mb-2">You&apos;re All Set!</h3>
            <p className="text-muted-foreground mb-4">Your account is verified with Tier 3 Premium access.</p>
            <p className="text-lg font-semibold text-primary">Wallet Limit: ₦1,000,000</p>
          </div>
        )}

        {/* Need Help */}
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
          <p className="text-sm text-primary">
            <strong>Need Help?</strong> Contact support at <a href="mailto:support@blissdigitals.com" className="text-primary hover:underline">support@blissdigitals.com</a>
          </p>
        </div>
      </div>
    );
  }


  function SupportSection() {
    return (
      <div className="space-y-6">
        <div className="mb-7 flex flex-wrap items-end justify-between gap-4 border-b border-border pb-5">
          <div><p className="text-sm text-muted-foreground">Get help from our team</p><h1 className="mt-1 text-3xl font-extrabold leading-tight tracking-tight lg:text-[38px]">Support channels</h1></div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* WhatsApp */}
          <a 
            href={supportUrls.whatsapp_support_url}
            target="_blank" 
            rel="noopener noreferrer"
            data-testid="support-whatsapp-link"
            className="bg-gradient-to-br from-green-50 to-emerald-100 p-6 rounded-lg border border-primary hover:shadow-lg transition-all hover:-translate-y-1 flex flex-col items-center text-center"
          >
            <div className="w-14 h-14 bg-primary rounded-full flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>
            <h3 className="font-bold text-foreground mb-1">WhatsApp</h3>
            <p className="text-sm text-muted-foreground">Chat with us on WhatsApp</p>
            <span className="mt-2 text-xs text-primary font-semibold">Click to open</span>
          </a>

          {/* Telegram */}
          <a 
            href={supportUrls.telegram_support_url}
            target="_blank" 
            rel="noopener noreferrer"
            data-testid="support-telegram-link"
            className="bg-primary p-6 rounded-lg border border-primary/30 hover:shadow-lg transition-all hover:-translate-y-1 flex flex-col items-center text-center"
          >
            <div className="w-14 h-14 bg-primary rounded-full flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
              </svg>
            </div>
            <h3 className="font-bold text-foreground mb-1">Telegram</h3>
            <p className="text-sm text-muted-foreground">Join our Telegram channel</p>
            <span className="mt-2 text-xs text-primary font-semibold">Click to open</span>
          </a>

          {/* Email */}
          <a 
            href={`mailto:${supportUrls.support_email}`}
            data-testid="support-email-link"
            className="bg-primary p-6 rounded-lg border border-primary/30 hover:shadow-lg transition-all hover:-translate-y-1 flex flex-col items-center text-center"
          >
            <div className="w-14 h-14 bg-primary rounded-full flex items-center justify-center mb-4">
              <Mail className="w-7 h-7 text-white" />
            </div>
            <h3 className="font-bold text-foreground mb-1">Email</h3>
            <p className="text-sm text-muted-foreground">{supportUrls.support_email}</p>
            <span className="mt-2 text-xs text-primary font-semibold">Send email</span>
          </a>
        </div>

        {/* FAQ or additional info */}
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mt-6">
          <h4 className="font-semibold text-amber-600 mb-2">Response Time</h4>
          <p className="text-sm text-amber-600">We typically respond within 1-2 hours during business hours (9AM - 9PM WAT).</p>
        </div>
      </div>
    );
  }

  // ============ RESELLER SECTION ============
  function ResellerSection() {
    const [showApiKey, setShowApiKey] = useState(false);
    const [registering, setRegistering] = useState(false);
    const [upgrading, setUpgrading] = useState(false);
    // Use parent state for showDocs to persist across navigations
    const showDocs = showResellerDocs;
    const setShowDocs = setShowResellerDocs;

    // Fetch reseller data when section mounts (only if not already fetched)
    useEffect(() => {
      if (!resellerFetched) {
        fetchResellerData();
      }
    }, []); // Empty dependency - only run once on mount

    const handleRegister = async () => {
      setRegistering(true);
      try {
        await axios.post(`${API}/api/reseller/register`, {}, axiosConfig);
        toast.success('Registered as reseller!');
        // Force refetch
        setResellerFetched(false);
        setResellerLoading(true);
        setTimeout(() => fetchResellerData(), 500);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to register');
      } finally {
        setRegistering(false);
      }
    };

    const handleUpgrade = async (planName) => {
      if (!window.confirm(`Upgrade to ${planName} plan? Monthly fee will be deducted from your balance.`)) return;
      setUpgrading(true);
      try {
        await axios.post(`${API}/api/reseller/upgrade?plan_name=${planName}`, {}, axiosConfig);
        toast.success(`Upgraded to ${planName} plan!`);
        // Force refetch
        setResellerFetched(false);
        setResellerLoading(true);
        setTimeout(() => {
          fetchResellerData();
          fetchProfile();
        }, 500);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to upgrade');
      } finally {
        setUpgrading(false);
      }
    };

    const copyToClipboard = (text) => {
      navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard!');
    };

    const refreshOrders = async () => {
      try {
        const res = await axios.get(`${API}/api/reseller/orders?limit=20`, axiosConfig);
        setResellerOrders(res.data.orders || []);
        toast.success('Orders refreshed');
      } catch (err) {
        toast.error('Failed to refresh orders');
      }
    };

    if (resellerLoading) {
      return (
        <div className="space-y-6">
          <p className="text-xs text-muted-foreground">API access and plans</p><h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-foreground">Reseller portal</h1>
          <div className="bg-card p-8 rounded-lg border shadow-sm text-center">
            <RefreshCw className="w-8 h-8 mx-auto text-muted-foreground animate-spin" />
            <p className="text-sm text-muted-foreground mt-2">Loading...</p>
          </div>
        </div>
      );
    }

    // API Documentation Component - 5sim Style
    const ApiDocumentation = () => {
      // Use admin-configurable API URL
      const API_BASE_URL = `${resellerApiBaseUrl}/api/reseller/v1`;
      
      const [activeEndpoint, setActiveEndpoint] = useState('balance');
      const [activeTab, setActiveTab] = useState('shell');
      
      const endpoints = [
        { id: 'balance', name: 'Balance', method: 'GET' },
        { id: 'servers', name: 'Servers', method: 'GET' },
        { id: 'countries', name: 'Countries', method: 'GET' },
        { id: 'services', name: 'Services', method: 'GET' },
        { id: 'buy', name: 'Purchase', method: 'POST' },
        { id: 'status', name: 'Order Status', method: 'GET' },
        { id: 'cancel', name: 'Cancel Order', method: 'POST' },
      ];
      
      const apiKey = resellerProfile?.api_key || 'your_api_key';
      
      const endpointData = {
        balance: {
          title: 'Balance',
          method: 'GET',
          path: '/balance',
          description: 'Provides reseller wallet balance and plan information.',
          headers: ['Authorization: Bearer $token', 'Accept: application/json'],
          responseFields: [
            { name: 'success', type: 'boolean', desc: 'Request status' },
            { name: 'balance_ngn', type: 'number', desc: 'Balance in Naira' },
            { name: 'balance_usd', type: 'number', desc: 'Balance in USD' },
            { name: 'currency', type: 'string', desc: 'Primary currency' },
            { name: 'plan', type: 'string', desc: 'Current subscription plan' },
          ],
          examples: {
            shell: `curl "${API_BASE_URL}/balance" \\
  -H "X-API-KEY: ${apiKey}"`,
            python: `import requests

response = requests.get(
    "${API_BASE_URL}/balance",
    headers={"X-API-KEY": "${apiKey}"}
)
print(response.json())`,
            php: `<?php
$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, "${API_BASE_URL}/balance");
curl_setopt($ch, CURLOPT_HTTPHEADER, ["X-API-KEY: ${apiKey}"]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);
curl_close($ch);
print_r(json_decode($response, true));`,
          },
          response: `{
  "success": true,
  "balance_ngn": 50000.00,
  "balance_usd": 33.33,
  "currency": "NGN",
  "plan": "Free"
}`,
        },
        servers: {
          title: 'Servers',
          method: 'GET',
          path: '/servers',
          description: 'Returns list of available SMS servers.',
          headers: ['X-API-KEY: $api_key', 'Accept: application/json'],
          responseFields: [
            { name: 'success', type: 'boolean', desc: 'Request status' },
            { name: 'servers', type: 'array', desc: 'List of available servers' },
            { name: 'servers[].key', type: 'string', desc: 'Server identifier' },
            { name: 'servers[].scope', type: 'string', desc: 'Coverage scope (US_ONLY/GLOBAL)' },
            { name: 'servers[].description', type: 'string', desc: 'Server description' },
          ],
          examples: {
            shell: `curl "${API_BASE_URL}/servers" \\
  -H "X-API-KEY: ${apiKey}"`,
            python: `import requests

response = requests.get(
    "${API_BASE_URL}/servers",
    headers={"X-API-KEY": "${apiKey}"}
)
print(response.json())`,
            php: `<?php
$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, "${API_BASE_URL}/servers");
curl_setopt($ch, CURLOPT_HTTPHEADER, ["X-API-KEY: ${apiKey}"]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);
print_r(json_decode($response, true));`,
          },
          response: `{
  "success": true,
  "servers": [
    {"key": "usa", "scope": "US_ONLY", "description": "United States numbers"},
    {"key": "all_country_1", "scope": "GLOBAL", "description": "All countries - Primary"},
    {"key": "all_country_2", "scope": "GLOBAL", "description": "All countries - Secondary"}
  ]
}`,
        },
        countries: {
          title: 'Countries',
          method: 'GET',
          path: '/countries?server={server}',
          description: 'Returns available countries for a specific server.',
          headers: ['X-API-KEY: $api_key', 'Accept: application/json'],
          responseFields: [
            { name: 'success', type: 'boolean', desc: 'Request status' },
            { name: 'server', type: 'string', desc: 'Selected server' },
            { name: 'countries', type: 'array', desc: 'List of countries' },
            { name: 'countries[].code', type: 'string', desc: 'ISO country code' },
            { name: 'countries[].name', type: 'string', desc: 'Country name' },
          ],
          examples: {
            shell: `curl "${API_BASE_URL}/countries?server=all_country_1" \\
  -H "X-API-KEY: ${apiKey}"`,
            python: `import requests

response = requests.get(
    "${API_BASE_URL}/countries",
    params={"server": "all_country_1"},
    headers={"X-API-KEY": "${apiKey}"}
)
print(response.json())`,
            php: `<?php
$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, "${API_BASE_URL}/countries?server=all_country_1");
curl_setopt($ch, CURLOPT_HTTPHEADER, ["X-API-KEY: ${apiKey}"]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);
print_r(json_decode($response, true));`,
          },
          response: `{
  "success": true,
  "server": "all_country_1",
  "countries": [
    {"code": "US", "name": "United States"},
    {"code": "UK", "name": "United Kingdom"},
    {"code": "RU", "name": "Russia"}
  ]
}`,
        },
        services: {
          title: 'Services',
          method: 'GET',
          path: '/services?server={server}&country={country}',
          description: 'Returns available services with pricing for the selected server.',
          headers: ['X-API-KEY: $api_key', 'Accept: application/json'],
          responseFields: [
            { name: 'success', type: 'boolean', desc: 'Request status' },
            { name: 'server', type: 'string', desc: 'Selected server' },
            { name: 'services', type: 'array', desc: 'List of services' },
            { name: 'services[].code', type: 'string', desc: 'Service code' },
            { name: 'services[].name', type: 'string', desc: 'Service name' },
            { name: 'services[].price_ngn', type: 'number', desc: 'Price in Naira' },
            { name: 'services[].price_usd', type: 'number', desc: 'Price in USD' },
            { name: 'services[].available', type: 'boolean', desc: 'Availability status' },
          ],
          examples: {
            shell: `curl "${API_BASE_URL}/services?server=usa" \\
  -H "X-API-KEY: ${apiKey}"`,
            python: `import requests

response = requests.get(
    "${API_BASE_URL}/services",
    params={"server": "usa"},
    headers={"X-API-KEY": "${apiKey}"}
)
print(response.json())`,
            php: `<?php
$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, "${API_BASE_URL}/services?server=usa");
curl_setopt($ch, CURLOPT_HTTPHEADER, ["X-API-KEY: ${apiKey}"]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);
print_r(json_decode($response, true));`,
          },
          response: `{
  "success": true,
  "server": "usa",
  "services": [
    {"code": "wa", "name": "WhatsApp", "price_ngn": 850, "price_usd": 0.57, "available": true},
    {"code": "tg", "name": "Telegram", "price_ngn": 650, "price_usd": 0.43, "available": true}
  ]
}`,
        },
        buy: {
          title: 'Purchase Number',
          method: 'POST',
          path: '/buy',
          description: 'Purchase a virtual number for SMS verification.',
          headers: ['X-API-KEY: $api_key', 'Content-Type: application/json'],
          requestBody: [
            { name: 'server', type: 'string', required: true, desc: 'Server key (usa, all_country_1, all_country_2)' },
            { name: 'service', type: 'string', required: true, desc: 'Service code from services list' },
            { name: 'country', type: 'string', required: false, desc: 'Country code (required for global servers)' },
            { name: 'price', type: 'number', required: true, desc: 'Price from services list' },
          ],
          responseFields: [
            { name: 'success', type: 'boolean', desc: 'Request status' },
            { name: 'order_id', type: 'string', desc: 'Internal order ID' },
            { name: 'provider_order_id', type: 'string', desc: 'Provider order ID (use for status/cancel)' },
            { name: 'phone_number', type: 'string', desc: 'Purchased phone number' },
            { name: 'status', type: 'string', desc: 'Order status (active/completed/cancelled)' },
            { name: 'price_charged_ngn', type: 'number', desc: 'Amount charged' },
          ],
          examples: {
            shell: `curl -X POST "${API_BASE_URL}/buy" \\
  -H "X-API-KEY: ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"server": "usa", "service": "wa", "price": 850}'`,
            python: `import requests

response = requests.post(
    "${API_BASE_URL}/buy",
    headers={"X-API-KEY": "${apiKey}"},
    json={"server": "usa", "service": "wa", "price": 850}
)
print(response.json())`,
            php: `<?php
$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, "${API_BASE_URL}/buy");
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    "X-API-KEY: ${apiKey}",
    "Content-Type: application/json"
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([
    "server" => "usa",
    "service" => "wa",
    "price" => 850
]));
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);
print_r(json_decode($response, true));`,
          },
          response: `{
  "success": true,
  "order_id": "abc123",
  "provider_order_id": "12345",
  "phone_number": "+12025551234",
  "server": "usa",
  "service": "wa",
  "price_charged_ngn": 850,
  "status": "active"
}`,
        },
        status: {
          title: 'Order Status',
          method: 'GET',
          path: '/status?provider_order_id={id}',
          description: 'Check order status and retrieve OTP code.',
          headers: ['X-API-KEY: $api_key', 'Accept: application/json'],
          responseFields: [
            { name: 'success', type: 'boolean', desc: 'Request status' },
            { name: 'order_id', type: 'string', desc: 'Internal order ID' },
            { name: 'provider_order_id', type: 'string', desc: 'Provider order ID' },
            { name: 'phone_number', type: 'string', desc: 'Phone number' },
            { name: 'status', type: 'string', desc: 'Current status' },
            { name: 'otp', type: 'string', desc: 'OTP code (if received)' },
            { name: 'sms_text', type: 'string', desc: 'Full SMS text' },
          ],
          examples: {
            shell: `curl "${API_BASE_URL}/status?provider_order_id=12345" \\
  -H "X-API-KEY: ${apiKey}"`,
            python: `import requests
import time

# Poll for OTP
for _ in range(30):
    response = requests.get(
        "${API_BASE_URL}/status",
        params={"provider_order_id": "12345"},
        headers={"X-API-KEY": "${apiKey}"}
    )
    data = response.json()
    if data.get("otp"):
        print("OTP:", data["otp"])
        break
    time.sleep(10)`,
            php: `<?php
$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, "${API_BASE_URL}/status?provider_order_id=12345");
curl_setopt($ch, CURLOPT_HTTPHEADER, ["X-API-KEY: ${apiKey}"]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);
print_r(json_decode($response, true));`,
          },
          response: `{
  "success": true,
  "order_id": "abc123",
  "provider_order_id": "12345",
  "phone_number": "+12025551234",
  "status": "completed",
  "otp": "123456",
  "sms_text": "Your WhatsApp code is 123456"
}`,
        },
        cancel: {
          title: 'Cancel Order',
          method: 'POST',
          path: '/cancel',
          description: 'Cancel an active order and receive refund (before OTP is received).',
          headers: ['X-API-KEY: $api_key', 'Content-Type: application/json'],
          requestBody: [
            { name: 'provider_order_id', type: 'string', required: true, desc: 'Provider order ID from purchase response' },
          ],
          responseFields: [
            { name: 'success', type: 'boolean', desc: 'Request status' },
            { name: 'message', type: 'string', desc: 'Result message' },
            { name: 'refund_amount_ngn', type: 'number', desc: 'Refunded amount' },
          ],
          examples: {
            shell: `curl -X POST "${API_BASE_URL}/cancel" \\
  -H "X-API-KEY: ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"provider_order_id": "12345"}'`,
            python: `import requests

response = requests.post(
    "${API_BASE_URL}/cancel",
    headers={"X-API-KEY": "${apiKey}"},
    json={"provider_order_id": "12345"}
)
print(response.json())`,
            php: `<?php
$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, "${API_BASE_URL}/cancel");
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    "X-API-KEY: ${apiKey}",
    "Content-Type: application/json"
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([
    "provider_order_id" => "12345"
]));
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);
print_r(json_decode($response, true));`,
          },
          response: `{
  "success": true,
  "message": "Order cancelled and refunded",
  "refund_amount_ngn": 850
}`,
        },
      };
      
      const currentEndpoint = endpointData[activeEndpoint];
      
      return (
        <div className="space-y-4">
          {/* Back Button and Header */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowDocs(false)}
              className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
            >
              ← Back to Portal
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center">
                <Server className="w-4 h-4 text-white" />
              </div>
              <span className="font-semibold text-sm text-foreground">API Documentation</span>
            </div>
          </div>

          {/* Endpoint Selector */}
          <div className="rounded-2xl bg-card shadow-card p-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Select Endpoint</p>
            <div className="flex flex-wrap gap-2">
              {endpoints.map((ep) => (
                <button
                  key={ep.id}
                  onClick={() => setActiveEndpoint(ep.id)}
                  className={`px-3 py-2 text-xs rounded-md transition-colors flex items-center gap-2 ${
                    activeEndpoint === ep.id 
                      ? 'bg-primary text-white' 
                      : 'bg-muted text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <span>{ep.name}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${
                    ep.method === 'GET' ? 'bg-primary text-white' : 'bg-primary text-white'
                  }`}>{ep.method}</span>
                </button>
              ))}
            </div>
          </div>

          {/* API Key Info */}
          <div className="bg-gray-900 rounded-lg p-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Authentication</p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Header:</span>
              <code className="text-primary text-xs bg-gray-800 px-2 py-1 rounded">X-API-KEY: {apiKey.substring(0, 16)}...</code>
            </div>
          </div>

          {/* Endpoint Details */}
          <div className="rounded-2xl bg-card shadow-card p-4 sm:p-6">
            <div className="mb-6">
              <h1 className="text-xl sm:text-2xl font-bold text-foreground">{currentEndpoint.title}</h1>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className={`px-2 py-1 text-xs font-bold rounded ${
                  currentEndpoint.method === 'GET' 
                    ? 'bg-primary/10 text-primary' 
                    : 'bg-primary/10 text-primary'
                }`}>{currentEndpoint.method}</span>
                <code className="text-xs sm:text-sm text-muted-foreground bg-muted px-2 py-1 rounded break-all">
                  {API_BASE_URL}{currentEndpoint.path}
                </code>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">{currentEndpoint.description}</p>
            </div>
            
            {/* Headers */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-foreground mb-2">Headers</h3>
              <ul className="list-disc list-inside text-sm text-muted-foreground">
                {currentEndpoint.headers.map((h, i) => (
                  <li key={i} className="break-all">{h}</li>
                ))}
              </ul>
            </div>
            
            {/* Request Body (for POST endpoints) */}
            {currentEndpoint.requestBody && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-foreground mb-2">Request Body</h3>
                <div className="space-y-2">
                  {currentEndpoint.requestBody.map((field, i) => (
                    <div key={i} className="bg-muted/50 p-3 rounded-md">
                      <div className="flex items-center gap-2 flex-wrap">
                        <code className="text-xs font-mono text-foreground bg-card px-2 py-0.5 rounded">{field.name}</code>
                        <span className="text-xs text-muted-foreground">{field.type}</span>
                        {field.required && <span className="text-xs text-destructive">Required</span>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{field.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Response Fields */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-foreground mb-2">Response</h3>
              <div className="bg-primary/10 border border-primary rounded-md px-3 py-2 mb-3 flex items-center gap-2">
                <Check className="w-4 h-4 text-primary" />
                <span className="text-xs sm:text-sm text-primary font-medium">Status code: 200 OK</span>
              </div>
              <div className="space-y-2">
                {currentEndpoint.responseFields.map((field, i) => (
                  <div key={i} className="bg-muted/50 p-3 rounded-md">
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="text-xs font-mono text-foreground bg-card px-2 py-0.5 rounded">{field.name}</code>
                      <span className="text-xs text-muted-foreground">{field.type}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{field.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Code Examples */}
          <div className="bg-gray-900 rounded-lg overflow-hidden">
            {/* Tabs */}
            <div className="flex border-b border-gray-700">
              {['shell', 'python', 'php'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-3 text-xs font-medium capitalize transition-colors ${
                    activeTab === tab 
                      ? 'text-white border-b-2 border-primary' 
                      : 'text-muted-foreground hover:text-white'
                  }`}
                >
                  {tab === 'shell' ? 'Shell' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
            
            {/* Request Example */}
            <div className="p-4">
              <p className="text-xs text-muted-foreground mb-2">Request example</p>
              <div className="bg-[#151c24] rounded-md p-3 overflow-x-auto">
                <pre className="text-xs text-primary whitespace-pre-wrap font-mono break-all">
                  {currentEndpoint.examples[activeTab]}
                </pre>
              </div>
            </div>
            
            {/* Response Example */}
            <div className="p-4 pt-0">
              <p className="text-xs text-muted-foreground mb-2">Response example</p>
              <div className="bg-[#151c24] rounded-md p-3 overflow-x-auto">
                <pre className="text-xs font-mono whitespace-pre-wrap break-all">
                  {currentEndpoint.response.split('\n').map((line, i) => {
                    if (line.includes('"success"') || line.includes('"balance') || line.includes('"server"') || line.includes('"order_id"')) {
                      return <span key={i} className="text-primary">{line}{'\n'}</span>;
                    }
                    if (line.includes(': true') || line.includes(': false')) {
                      return <span key={i}><span className="text-muted-foreground">{line.split(':')[0]}:</span><span className="text-amber-600">{line.split(':')[1]}</span>{'\n'}</span>;
                    }
                    if (line.includes('": "')) {
                      const parts = line.split('": "');
                      return <span key={i}><span className="text-primary">{parts[0]}&quot;</span><span className="text-muted-foreground">: </span><span className="text-primary">&quot;{parts[1]}</span>{'\n'}</span>;
                    }
                    if (line.match(/: \d/)) {
                      const parts = line.split(': ');
                      return <span key={i}><span className="text-primary">{parts[0]}</span><span className="text-muted-foreground">: </span><span className="text-primary">{parts[1]}</span>{'\n'}</span>;
                    }
                    return <span key={i} className="text-muted-foreground">{line}{'\n'}</span>;
                  })}
                </pre>
              </div>
            </div>
          </div>
        </div>
      );
    };

    // Show docs if user clicked the button
    if (showDocs && resellerProfile?.is_reseller) {
      return <ApiDocumentation />;
    }

    // Not a reseller yet - show registration
    if (!resellerProfile?.is_reseller) {
      return (
        <div className="space-y-6">
          <p className="text-xs text-muted-foreground">API access and plans</p><h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-foreground">Reseller portal</h1>
          
          {/* Hero Card */}
          <div className="bg-primary rounded-lg p-6 text-white">
            <h3 className="text-2xl font-bold mb-2">Become a Reseller</h3>
            <p className="text-primary/70 text-sm mb-4">
              Access our API to resell SMS verification services. Get competitive pricing, 
              dedicated support, and comprehensive documentation.
            </p>
            <button
              onClick={handleRegister}
              disabled={registering}
              className="px-6 py-2.5 bg-card text-primary rounded-full font-semibold text-sm hover:bg-primary/10 transition-colors disabled:opacity-50"
            >
              {registering ? 'Registering...' : 'Register for Free'}
            </button>
          </div>

          {/* Plans */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Available Plans</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {resellerPlans.map((plan) => (
                <div key={plan.id} className={`bg-card rounded-lg border-2 p-5 ${plan.name === 'Pro' ? 'border-primary/30 ring-2 ring-primary' : 'border-border'}`}>
                  {plan.name === 'Pro' && (
                    <span className="px-2 py-0.5 bg-primary/10 text-primary text-[10px] font-bold rounded-full mb-2 inline-block">POPULAR</span>
                  )}
                  <h4 className="text-lg font-bold text-foreground">{plan.name}</h4>
                  <div className="mt-2 mb-3">
                    <span className="text-2xl font-bold text-foreground">₦{plan.monthly_fee_ngn?.toLocaleString()}</span>
                    <span className="text-sm text-muted-foreground">/month</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">{plan.description}</p>
                  <div className="space-y-1.5">
                    {(plan.features || []).map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Check className="w-3.5 h-3.5 text-primary" />
                        <span>{f}</span>
                      </div>
                    ))}
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Check className="w-3.5 h-3.5 text-primary" />
                      <span>{Math.round((1 - plan.markup_multiplier) * 100)}% markup discount</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // Reseller dashboard
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-xs text-muted-foreground">API access and plans</p><h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-foreground">Reseller portal</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowDocs(true)}
              className="px-3 sm:px-4 py-2 bg-primary text-white rounded-md text-xs font-semibold hover:bg-primary/90"
            >
              API Documentation
            </button>
            <span className="px-2 sm:px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-semibold">
              {resellerProfile.plan} Plan
            </span>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-2xl bg-card shadow-card p-4">
            <p className="text-xs text-muted-foreground mb-1">Total Orders</p>
            <p className="text-2xl font-bold text-foreground">{resellerProfile.total_orders || 0}</p>
          </div>
          <div className="rounded-2xl bg-card shadow-card p-4">
            <p className="text-xs text-muted-foreground mb-1">Total Revenue</p>
            <p className="text-2xl font-bold text-primary">₦{(resellerProfile.total_revenue_ngn || 0).toLocaleString()}</p>
          </div>
          <div className="rounded-2xl bg-card shadow-card p-4">
            <p className="text-xs text-muted-foreground mb-1">Markup Discount</p>
            <p className="text-2xl font-bold text-primary">
              {resellerProfile.custom_markup_multiplier 
                ? `${Math.round((1 - resellerProfile.custom_markup_multiplier) * 100)}%`
                : `${Math.round((1 - (resellerProfile.plan_details?.markup_multiplier || 1)) * 100)}%`}
            </p>
          </div>
          <div className="rounded-2xl bg-card shadow-card p-4">
            <p className="text-xs text-muted-foreground mb-1">Your Balance</p>
            <p className="text-2xl font-bold text-foreground">₦{user.ngn_balance?.toLocaleString()}</p>
          </div>
        </div>

        {/* API Key Section */}
        <div className="bg-card rounded-lg border p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Key className="w-5 h-5 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">API Key</h3>
            </div>
            <button
              onClick={() => copyToClipboard(resellerProfile.api_key)}
              className="px-3 py-1.5 bg-primary/10 text-primary rounded-md text-xs font-semibold hover:bg-primary/10"
            >
              <Copy className="w-3.5 h-3.5 inline mr-1" />
              Copy
            </button>
          </div>
          <div className="flex items-center gap-2">
            <input
              type={showApiKey ? 'text' : 'password'}
              value={resellerProfile.api_key || ''}
              readOnly
              className="flex-1 px-4 py-2.5 bg-muted/50 border border-border rounded-md text-sm font-mono text-muted-foreground"
            />
            <button
              onClick={() => setShowApiKey(!showApiKey)}
              className="p-2.5 bg-muted rounded-md hover:bg-muted"
            >
              {showApiKey ? <EyeOff className="w-4 h-4 text-muted-foreground" /> : <Eye className="w-4 h-4 text-muted-foreground" />}
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">Use this key in the X-API-KEY header or as api_key query parameter</p>
        </div>

        {/* Quick Start */}
        <div className="bg-primary rounded-lg border border-primary/30 p-4 sm:p-5">
          <h3 className="text-sm font-semibold text-foreground mb-3">Quick Start</h3>
          <div className="bg-gray-900 rounded-md p-3 sm:p-4 overflow-x-auto max-w-full">
            <pre className="text-[10px] sm:text-xs text-primary whitespace-pre-wrap break-all">{`# 1. Get your balance
curl "${resellerApiBaseUrl}/api/reseller/v1/balance?api_key=${resellerProfile.api_key?.substring(0,8)}..."

# 2. List servers
curl "${resellerApiBaseUrl}/api/reseller/v1/servers"

# 3. Get services
curl "${resellerApiBaseUrl}/api/reseller/v1/services?server=usa"

# 4. Buy a number (POST)
curl -X POST "${resellerApiBaseUrl}/api/reseller/v1/buy" \\
  -H "X-API-KEY: YOUR_KEY" \\
  -d '{"server":"usa","service":"wa","price":850}'`}</pre>
          </div>
          <button
            onClick={() => setShowDocs(true)}
            className="mt-3 text-sm text-primary font-semibold hover:text-primary"
          >
            View Full Documentation →
          </button>
        </div>

        {/* Recent Orders */}
        <div className="bg-card rounded-lg border p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-foreground">Recent Orders</h3>
            <button onClick={refreshOrders} className="text-xs text-primary hover:text-primary font-semibold">
              <RefreshCw className="w-3.5 h-3.5 inline mr-1" />
              Refresh
            </button>
          </div>
          {resellerOrders.length === 0 ? (
            <div className="text-center py-8">
              <Server className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">No orders yet</p>
              <p className="text-xs text-muted-foreground">Orders made through API will appear here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[500px]">
                <thead className="border-b border-border bg-muted/50">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Service</th>
                    <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Phone</th>
                    <th className="text-left px-3 py-2 font-semibold text-muted-foreground">OTP</th>
                    <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Price</th>
                    <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Status</th>
                    <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {resellerOrders.map((order) => (
                    <tr key={order.id} className="border-b border-border hover:bg-muted/50">
                      <td className="px-3 py-2.5 font-medium text-foreground">{order.service_name || order.service}</td>
                      <td className="px-3 py-2.5 font-mono text-muted-foreground">{order.phone_number || '-'}</td>
                      <td className="px-3 py-2.5">
                        {order.otp ? (
                          <span className="font-mono font-bold text-primary">{order.otp}</span>
                        ) : (
                          <span className="text-muted-foreground">Waiting...</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground">₦{order.cost_ngn?.toFixed(2)}</td>
                      <td className="px-3 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          order.status === 'completed' ? 'bg-primary/10 text-primary' :
                          order.status === 'active' ? 'bg-primary/10 text-primary' :
                          order.status === 'refunded' ? 'bg-amber-50 text-amber-600' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {order.status?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground">{new Date(order.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}

                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Upgrade Plans */}
        {resellerProfile.plan !== 'Enterprise' && (
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3">Upgrade Plan</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {resellerPlans.filter(p => p.monthly_fee_ngn > (resellerProfile.plan_details?.monthly_fee_ngn || 0)).map((plan) => (
                <div key={plan.id} className="rounded-2xl bg-card shadow-card p-4">
                  <h4 className="font-semibold text-foreground">{plan.name}</h4>
                  <p className="text-lg font-bold text-foreground mt-1">₦{plan.monthly_fee_ngn?.toLocaleString()}<span className="text-xs text-muted-foreground">/mo</span></p>
                  <p className="text-xs text-muted-foreground mt-1">{Math.round((1 - plan.markup_multiplier) * 100)}% markup discount</p>
                  <button
                    onClick={() => handleUpgrade(plan.name)}
                    disabled={upgrading}
                    className="w-full mt-3 px-4 py-2 bg-primary text-white rounded-md text-xs font-semibold hover:bg-primary/90 disabled:opacity-50"
                  >
                    {upgrading ? 'Upgrading...' : 'Upgrade'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ============ CONVERT CURRENCY SECTION ============
  function ConvertCurrencySection() {
    const [usdToNgnRate, setUsdToNgnRate] = useState(1650);
    const [ngnToUsdRate, setNgnToUsdRate] = useState(1500);
    const [convertAmount, setConvertAmount] = useState('');
    const [converting, setConverting] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [conversionDirection, setConversionDirection] = useState('usd-to-ngn'); // or 'ngn-to-usd'

    useEffect(() => {
      fetchExchangeRates();
      fetchConversionHistory();
    }, []);

    const fetchExchangeRates = async () => {
      try {
        const resp = await axios.get(`${API}/api/wallet/exchange-rate`, axiosConfig);
        setUsdToNgnRate(resp.data.usd_to_ngn_rate || 1650);
        setNgnToUsdRate(resp.data.ngn_to_usd_rate || 1500);
      } catch (err) {
        console.error('Failed to fetch exchange rates:', err);
      }
    };

    const fetchConversionHistory = async () => {
      setLoading(true);
      try {
        const resp = await axios.get(`${API}/api/transactions/list`, axiosConfig);
        const conversions = (resp.data.transactions || []).filter(t => t.type === 'currency_conversion' || t.type === 'conversion');
        setTransactions(conversions.slice(0, 10));
      } catch (err) {
        console.error('Failed to fetch conversion history:', err);
      }
      setLoading(false);
    };

    const handleConvert = async () => {
      if (!convertAmount || parseFloat(convertAmount) <= 0) return;
      
      const amount = parseFloat(convertAmount);
      
      if (conversionDirection === 'usd-to-ngn') {
        if (amount > (user?.usd_balance || 0)) {
          toast.error('Insufficient USD balance');
          return;
        }
      } else {
        if (amount > (user?.ngn_balance || 0)) {
          toast.error('Insufficient NGN balance');
          return;
        }
      }
      
      setConverting(true);
      try {
        if (conversionDirection === 'usd-to-ngn') {
          const resp = await axios.post(`${API}/api/wallet/convert-usd-to-ngn`, 
            { amount_usd: amount }, 
            axiosConfig
          );
          toast.success(`Successfully converted $${amount.toFixed(2)} to ₦${resp.data.amount_ngn_added?.toLocaleString()}`);
        } else {
          const resp = await axios.post(`${API}/api/wallet/convert-ngn-to-usd`, 
            { amount_ngn: amount }, 
            axiosConfig
          );
          toast.success(`Successfully converted ₦${amount.toLocaleString()} to $${resp.data.usd_received?.toFixed(2)}`);
        }
        setConvertAmount('');
        fetchProfile();
        fetchConversionHistory();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Conversion failed');
      }
      setConverting(false);
    };

    const previewAmount = convertAmount ? (
      conversionDirection === 'usd-to-ngn' 
        ? parseFloat(convertAmount) * usdToNgnRate
        : parseFloat(convertAmount) / ngnToUsdRate
    ) : 0;

    const sourceBalance = conversionDirection === 'usd-to-ngn' ? user?.usd_balance || 0 : user?.ngn_balance || 0;
    const sourceCurrency = conversionDirection === 'usd-to-ngn' ? 'USD' : 'NGN';
    const targetCurrency = conversionDirection === 'usd-to-ngn' ? 'NGN' : 'USD';
    const currentRate = conversionDirection === 'usd-to-ngn' ? usdToNgnRate : ngnToUsdRate;

    return (
      <div className="space-y-6">
        <div>
          <div className="mb-7 flex flex-wrap items-end justify-between gap-4 border-b border-border pb-5">
            <div><p className="text-sm text-muted-foreground">Between USD and NGN</p><h1 className="mt-1 text-3xl font-extrabold leading-tight tracking-tight lg:text-[38px]">Convert currency</h1></div>
          </div>
        </div>

        {/* Balance Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-2xl bg-card shadow-card p-5">
            <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">USD balance</p>
            <p className="mt-1 text-3xl font-extrabold tracking-tight text-foreground">${(user?.usd_balance || 0).toFixed(2)}</p>
          </div>
          <div className="rounded-2xl bg-card shadow-card p-5">
            <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">NGN balance</p>
            <p className="mt-1 text-3xl font-extrabold tracking-tight text-foreground">₦{(user?.ngn_balance || 0).toLocaleString()}</p>
          </div>
        </div>

        {/* Conversion Direction Toggle */}
        <div className="bg-card rounded-lg border border-border p-4 shadow-sm">
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Direction</p>
          <div className="grid grid-cols-2 gap-2 bg-muted rounded-md p-1">
            <button
              onClick={() => { setConversionDirection('usd-to-ngn'); setConvertAmount(''); }}
              className={`h-11 px-4 rounded-md font-bold text-sm transition-colors flex items-center justify-center gap-2 ${
                conversionDirection === 'usd-to-ngn'
                  ? 'bg-card text-foreground shadow-sm border border-border'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="text-lg">$</span> → <span className="text-lg">₦</span>
              <span className="hidden sm:inline ml-1">USD to NGN</span>
            </button>
            <button
              onClick={() => { setConversionDirection('ngn-to-usd'); setConvertAmount(''); }}
              className={`h-11 px-4 rounded-md font-bold text-sm transition-colors flex items-center justify-center gap-2 ${
                conversionDirection === 'ngn-to-usd'
                  ? 'bg-card text-foreground shadow-sm border border-border'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              style={conversionDirection === 'ngn-to-usd' ? { backgroundColor: branding.accent_color_hex || '#7c3aed' } : {}}
            >
              <span className="text-lg">₦</span> → <span className="text-lg">$</span>
              <span className="hidden sm:inline ml-1">NGN to USD</span>
            </button>
          </div>
        </div>

        {/* Exchange Rate Info */}
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-md border border-border bg-muted/40 px-4 py-3">
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">USD → NGN</p>
              <p className="mt-0.5 text-base font-bold text-foreground">$1 = ₦{usdToNgnRate.toLocaleString()}</p>
            </div>
            <div className="rounded-md border border-border bg-muted/40 px-4 py-3">
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">NGN → USD</p>
              <p className="mt-0.5 text-base font-bold text-foreground">₦{ngnToUsdRate.toLocaleString()} = $1</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Rates are set by admin and may change.</p>
        </div>

        {/* Conversion Form */}
        <div className="rounded-3xl bg-card shadow-card p-6">
          <h3 className="font-bold text-foreground mb-4">
            Convert {sourceCurrency} to {targetCurrency}
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                Amount ({sourceCurrency})
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {conversionDirection === 'usd-to-ngn' ? '$' : '₦'}
                </span>
                <input
                  type="number"
                  value={convertAmount}
                  onChange={(e) => setConvertAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-8 pr-4 py-3 border rounded-md text-lg"
                  max={sourceBalance}
                  step={conversionDirection === 'usd-to-ngn' ? '0.01' : '1'}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Available: {conversionDirection === 'usd-to-ngn' 
                  ? `$${sourceBalance.toFixed(2)}` 
                  : `₦${sourceBalance.toLocaleString()}`}
              </p>
            </div>

            {/* Quick Amount Buttons */}
            <div className="flex flex-wrap gap-2">
              {(conversionDirection === 'usd-to-ngn' 
                ? ['5', '10', '50', '100'] 
                : ['5000', '10000', '50000', '100000']
              ).map((preset) => (
                <button
                  key={preset}
                  onClick={() => setConvertAmount(preset)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    convertAmount === preset 
                      ? 'text-white' 
                      : 'bg-muted text-muted-foreground hover:bg-muted'
                  }`}
                  style={convertAmount === preset ? { backgroundColor: branding.primary_color_hex || '#059669' } : {}}
                >
                  {conversionDirection === 'usd-to-ngn' ? `$${preset}` : `₦${parseInt(preset).toLocaleString()}`}
                </button>
              ))}
            </div>

            {/* Preview */}
            {convertAmount && parseFloat(convertAmount) > 0 && (
              <div className="bg-primary/10 rounded-md p-4">
                <p className="text-sm text-muted-foreground">You will receive:</p>
                <p className="text-2xl font-bold text-primary">
                  {conversionDirection === 'usd-to-ngn' 
                    ? `₦${previewAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                    : `$${previewAmount.toFixed(2)}`}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Rate: {conversionDirection === 'usd-to-ngn' 
                    ? `$1 = ₦${currentRate.toLocaleString()}`
                    : `₦${currentRate.toLocaleString()} = $1`}
                </p>
              </div>
            )}

            <button
              onClick={handleConvert}
              disabled={converting || !convertAmount || parseFloat(convertAmount) <= 0 || parseFloat(convertAmount) > sourceBalance}
              className="w-full py-3 text-white rounded-md font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ backgroundColor: conversionDirection === 'usd-to-ngn' ? (branding.primary_color_hex || '#059669') : (branding.accent_color_hex || '#7c3aed') }}
            >
              {converting ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Converting...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5" />
                  Convert to {targetCurrency}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Recent Conversions */}
        <div className="rounded-3xl bg-card shadow-card p-6">
          <h3 className="font-bold text-foreground mb-4">Recent Conversions</h3>
          
          {loading ? (
            <div className="text-center py-4">
              <RefreshCw className="w-6 h-6 mx-auto text-muted-foreground animate-spin" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8">
              <RefreshCw className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground">No conversions yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.map((tx, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-md">
                  <div>
                    <p className="font-medium text-foreground">
                      {tx.amount_usd !== undefined && tx.amount_usd < 0
                        ? `$${Math.abs(tx.amount_usd || 0).toFixed(2)} → ₦${(tx.amount_ngn || tx.amount || 0).toLocaleString()}`
                        : tx.metadata?.usd_received 
                          ? `₦${(tx.amount || 0).toLocaleString()} → $${tx.metadata.usd_received.toFixed(2)}`
                          : `$${Math.abs(tx.amount_usd || 0).toFixed(2)} → ₦${(tx.amount_ngn || 0).toLocaleString()}`
                      }
                    </p>
                    <p className="text-xs text-muted-foreground">Rate: ₦{(tx.exchange_rate || tx.metadata?.rate || usdToNgnRate)?.toLocaleString()}/USD</p>
                  </div>
                  <div className="text-right">
                    <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">Completed</span>
                    <p className="text-xs text-muted-foreground mt-1">{new Date(tx.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============ GIFT CARDS SECTION ============
  function GiftCardsSection() {
    const [countries, setCountries] = useState([]);
    const [products, setProducts] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [orderHistory, setOrderHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderDetails, setOrderDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [orderForm, setOrderForm] = useState({
      amount: '',
      recipient_email: '',
      recipient_phone: '',
      sender_name: user?.full_name || ''
    });
    const [ordering, setOrdering] = useState(false);
    const [exchangeRate, setExchangeRate] = useState(1650);
    const [countrySearch, setCountrySearch] = useState('');
    const [showCountryDropdown, setShowCountryDropdown] = useState(false);

    // Fetch order details including redeem code
    const fetchOrderDetails = async (order) => {
      setSelectedOrder(order);
      setLoadingDetails(true);
      try {
        const transactionId = order.transaction_id;
        if (transactionId && order.status === 'SUCCESSFUL') {
          const resp = await axios.get(`${API}/api/giftcards/redeem-code/${transactionId}`, axiosConfig);
          if (resp.data.success) {
            setOrderDetails({ ...order, cards: resp.data.cards });
          } else {
            setOrderDetails(order);
          }
        } else {
          setOrderDetails(order);
        }
      } catch (err) {
        console.error('Failed to fetch order details:', err);
        setOrderDetails(order);
      }
      setLoadingDetails(false);
    };

    // Fetch countries on mount
    useEffect(() => {
      fetchCountries();
      fetchExchangeRate();
    }, []);

    // Fetch products when country changes
    useEffect(() => {
      fetchProducts();
    }, [selectedCountry]);

    const fetchCountries = async () => {
      try {
        const resp = await axios.get(`${API}/api/giftcards/countries`, axiosConfig);
        setCountries(resp.data.countries || []);
      } catch (err) {
        console.error('Failed to fetch countries:', err);
      }
    };

    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = { size: 100 };
        if (selectedCountry) params.country_code = selectedCountry;
        const resp = await axios.get(`${API}/api/giftcards/products`, { ...axiosConfig, params });
        setProducts(resp.data.products || []);
      } catch (err) {
        console.error('Failed to fetch products:', err);
      }
      setLoading(false);
    };

    const fetchExchangeRate = async () => {
      try {
        const resp = await axios.get(`${API}/api/wallet/exchange-rate`, axiosConfig);
        setExchangeRate(resp.data.usd_to_ngn_rate || 1650);
      } catch (err) {
        console.error('Failed to fetch exchange rate:', err);
      }
    };

    const fetchOrderHistory = async () => {
      try {
        const resp = await axios.get(`${API}/api/giftcards/orders`, axiosConfig);
        setOrderHistory(resp.data.orders || []);
      } catch (err) {
        console.error('Failed to fetch order history:', err);
      }
    };

    const handlePlaceOrder = async () => {
      if (!selectedProduct || !orderForm.amount || !orderForm.recipient_email || !orderForm.recipient_phone) {
        toast.error('Please fill all required fields');
        return;
      }
      setOrdering(true);
      try {
        const resp = await axios.post(`${API}/api/giftcards/order`, {
          product_id: selectedProduct.productId,
          quantity: 1,
          unit_price: parseFloat(orderForm.amount),
          recipient_email: orderForm.recipient_email,
          recipient_phone: orderForm.recipient_phone,
          sender_name: orderForm.sender_name || undefined
        }, axiosConfig);
        
        toast.success(`Order placed! Transaction ID: ${resp.data.transaction_id}. Amount charged: ₦${resp.data.amount_charged_ngn.toLocaleString()}`);
        setSelectedProduct(null);
        setOrderForm({ amount: '', recipient_email: '', recipient_phone: '', sender_name: user?.full_name || '' });
        fetchProfile();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Order failed');
      }
      setOrdering(false);
    };

    const filteredProducts = products.filter(p => 
      !searchQuery || 
      p.productName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.brand?.brandName?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Order Details Modal
    if (selectedProduct) {
      const productDenominations = selectedProduct.fixedRecipientDenominations || [];
      const isFixed = productDenominations.length > 0;
      
      return (
        <div className="space-y-4">
          <button 
            onClick={() => setSelectedProduct(null)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            ← Back to Gift Cards
          </button>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Product Info */}
            <div className="rounded-3xl bg-card shadow-card p-6">
              <div className="flex items-start gap-4">
                {selectedProduct.logoUrls?.[0] && (
                  <img src={selectedProduct.logoUrls[0]} alt="" className="w-20 h-20 object-contain rounded-md bg-muted/50" />
                )}
                <div>
                  <h2 className="text-xl font-bold text-foreground">{selectedProduct.productName}</h2>
                  <p className="text-sm text-muted-foreground">{selectedProduct.brand?.brandName}</p>
                  <p className="text-sm text-muted-foreground">{selectedProduct.country?.name}</p>
                </div>
              </div>
              
              {selectedProduct.redeemInstruction?.verbose && (
                <div className="mt-4 p-3 bg-muted/50 rounded-md">
                  <p className="text-xs text-muted-foreground">{selectedProduct.redeemInstruction.verbose}</p>
                </div>
              )}
              
              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Card Currency</span>
                  <span className="font-medium">{selectedProduct.recipientCurrencyCode}</span>
                </div>
                {selectedProduct.recipientCurrencyCode !== 'USD' && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{selectedProduct.recipientCurrencyCode} to USD</span>
                    <span className="font-medium">1 {selectedProduct.recipientCurrencyCode} = ${(selectedProduct.currency_to_usd_rate || 1).toFixed(4)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">USD to NGN Rate</span>
                  <span className="font-medium">₦{(selectedProduct.admin_usd_to_ngn_rate || 1650).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm border-t pt-2">
                  <span className="text-muted-foreground font-medium">Final Rate</span>
                  <span className="font-bold text-primary">1 {selectedProduct.recipientCurrencyCode} = ₦{(selectedProduct.final_ngn_rate || exchangeRate).toLocaleString()}</span>
                </div>
                {selectedProduct.senderFee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Service Fee</span>
                    <span className="font-medium">${selectedProduct.senderFee}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Order Form */}
            <div className="rounded-3xl bg-card shadow-card p-6">
              <h3 className="font-bold text-foreground mb-4">Purchase Details</h3>
              
              <div className="space-y-4">
                {/* Amount Selection */}
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Gift Card Value ({selectedProduct.recipientCurrencyCode})
                  </label>
                  {isFixed ? (
                    <div className="grid grid-cols-3 gap-2">
                      {productDenominations.map((val) => (
                        <button
                          key={val}
                          onClick={() => setOrderForm({ ...orderForm, amount: val.toString() })}
                          className={`px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                            orderForm.amount === val.toString()
                              ? 'bg-primary text-white border-primary'
                              : 'bg-card text-muted-foreground hover:bg-muted/50'
                          }`}
                        >
                          {selectedProduct.recipientCurrencyCode} {val}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <input
                      type="number"
                      value={orderForm.amount}
                      onChange={(e) => setOrderForm({ ...orderForm, amount: e.target.value })}
                      placeholder={`Min: ${selectedProduct.minRecipientDenomination}, Max: ${selectedProduct.maxRecipientDenomination}`}
                      className="w-full px-3 py-2 border rounded-md text-sm"
                      min={selectedProduct.minRecipientDenomination}
                      max={selectedProduct.maxRecipientDenomination}
                    />
                  )}
                </div>
                
                {/* Recipient Email */}
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Recipient Email <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="email"
                    value={orderForm.recipient_email}
                    onChange={(e) => setOrderForm({ ...orderForm, recipient_email: e.target.value })}
                    placeholder="recipient@example.com"
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Gift card will be sent to this email</p>
                </div>
                
                {/* Recipient Phone */}
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Recipient Phone <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="tel"
                    value={orderForm.recipient_phone}
                    onChange={(e) => setOrderForm({ ...orderForm, recipient_phone: e.target.value })}
                    placeholder="+234..."
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                </div>
                
                {/* Sender Name (optional) */}
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Your Name (optional)
                  </label>
                  <input
                    type="text"
                    value={orderForm.sender_name}
                    onChange={(e) => setOrderForm({ ...orderForm, sender_name: e.target.value })}
                    placeholder="Sender name"
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                </div>
                
                {/* Price Summary */}
                {orderForm.amount && (
                  <div className="bg-primary/10 rounded-md p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Card Value</span>
                      <span>{selectedProduct.recipientCurrencyCode} {orderForm.amount}</span>
                    </div>
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Rate</span>
                      <span>1 {selectedProduct.recipientCurrencyCode} = ₦{(selectedProduct.final_ngn_rate || exchangeRate).toLocaleString()}</span>
                    </div>
                    {selectedProduct.senderFee > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Service Fee</span>
                        <span>${selectedProduct.senderFee}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-primary pt-2 border-t border-primary">
                      <span>Total (NGN)</span>
                      <span>₦{(parseFloat(orderForm.amount) * (selectedProduct.final_ngn_rate || exchangeRate) + (selectedProduct.senderFee || 0) * (selectedProduct.final_ngn_rate || exchangeRate)).toLocaleString()}</span>
                    </div>
                  </div>
                )}
                
                <button
                  onClick={handlePlaceOrder}
                  disabled={ordering || !orderForm.amount || !orderForm.recipient_email || !orderForm.recipient_phone}
                  className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground font-bold text-sm shadow-cta hover:brightness-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {ordering ? 'Processing...' : 'Purchase Gift Card'}
                </button>
                
                <p className="text-xs text-muted-foreground text-center">
                  Payment will be deducted from your NGN wallet balance
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Order Details View
    if (selectedOrder) {
      return (
        <div className="space-y-4">
          <button 
            onClick={() => { setSelectedOrder(null); setOrderDetails(null); }}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            ← Back to Order History
          </button>
          
          <div className="bg-card rounded-lg border overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-primary p-6 text-white">
              <h2 className="text-xl font-bold">{selectedOrder.product_name}</h2>
              <p className="text-white/80">{selectedOrder.brand_name}</p>
            </div>
            
            <div className="p-6 space-y-6">
              {loadingDetails ? (
                <div className="text-center py-8">
                  <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
                  <p className="text-muted-foreground mt-2">Loading details...</p>
                </div>
              ) : (
                <>
                  {/* Order Status */}
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-md">
                    <span className="text-muted-foreground">Status</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      selectedOrder.status === 'SUCCESSFUL' ? 'bg-primary/10 text-primary' :
                      selectedOrder.status === 'PENDING' ? 'bg-amber-50 text-amber-600' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {selectedOrder.status}
                    </span>
                  </div>
                  
                  {/* Redeem Code Section */}
                  {orderDetails?.cards && orderDetails.cards.length > 0 && (
                    <div className="border-2 border-primary rounded-md p-4 bg-primary/10">
                      <h3 className="font-semibold text-primary mb-3 flex items-center gap-2">
                        <Gift className="w-5 h-5" />
                        Redeem Code{orderDetails.cards.length > 1 ? 's' : ''}
                      </h3>
                      {orderDetails.cards.map((card, idx) => (
                        <div key={idx} className="bg-card rounded-md p-4 mb-2 border">
                          {card.cardNumber && (
                            <div className="mb-2">
                              <p className="text-xs text-muted-foreground">Card Number / Code</p>
                              <p className="font-mono text-lg font-bold text-primary select-all">{card.cardNumber}</p>
                            </div>
                          )}
                          {card.pinCode && (
                            <div className="mb-2">
                              <p className="text-xs text-muted-foreground">PIN</p>
                              <p className="font-mono text-lg font-bold text-primary select-all">{card.pinCode}</p>
                            </div>
                          )}
                          <button
                            onClick={() => {
                              const text = card.pinCode ? `Code: ${card.cardNumber}, PIN: ${card.pinCode}` : card.cardNumber;
                              navigator.clipboard.writeText(text);
                              toast.success('Copied to clipboard!');
                            }}
                            className="mt-2 px-3 py-1 text-xs bg-primary text-white rounded hover:bg-primary"
                          >
                            Copy Code
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Order Details */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-foreground">Order Details</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="p-3 bg-muted/50 rounded-md">
                        <p className="text-muted-foreground">Transaction ID</p>
                        <p className="font-semibold">{selectedOrder.transaction_id}</p>
                      </div>
                      <div className="p-3 bg-muted/50 rounded-md">
                        <p className="text-muted-foreground">Amount Paid</p>
                        <p className="font-semibold text-primary">₦{selectedOrder.total_ngn?.toLocaleString()}</p>
                      </div>
                      <div className="p-3 bg-muted/50 rounded-md">
                        <p className="text-muted-foreground">Card Value</p>
                        <p className="font-semibold">${selectedOrder.unit_price} x {selectedOrder.quantity}</p>
                      </div>
                      <div className="p-3 bg-muted/50 rounded-md">
                        <p className="text-muted-foreground">Date</p>
                        <p className="font-semibold">{new Date(selectedOrder.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Recipient Details */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-foreground">Recipient Details</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div className="p-3 bg-muted/50 rounded-md">
                        <p className="text-muted-foreground">Email</p>
                        <p className="font-semibold">{selectedOrder.recipient_email}</p>
                      </div>
                      <div className="p-3 bg-muted/50 rounded-md">
                        <p className="text-muted-foreground">Phone</p>
                        <p className="font-semibold">{selectedOrder.recipient_phone}</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Order History View
    if (showHistory) {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => setShowHistory(false)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              ← Back to Gift Cards
            </button>
            <button onClick={fetchOrderHistory} className="text-sm text-primary hover:underline">
              Refresh
            </button>
          </div>
          
          <h2 className="text-xl font-bold text-foreground">Order History</h2>
          
          {orderHistory.length === 0 ? (
            <div className="bg-card rounded-lg border p-8 text-center">
              <Gift className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No gift card orders yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {orderHistory.map((order) => (
                <div key={order._id} className="rounded-2xl bg-card shadow-card p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-foreground">{order.product_name}</p>
                      <p className="text-sm text-muted-foreground">{order.brand_name}</p>
                      <p className="text-xs text-muted-foreground mt-1">To: {order.recipient_email}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-primary">₦{order.total_ngn?.toLocaleString()}</p>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${
                        order.status === 'SUCCESSFUL' ? 'bg-primary/10 text-primary' :
                        order.status === 'PENDING' ? 'bg-amber-50 text-amber-600' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {order.status}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t">
                    <p className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleString()}</p>
                    <button
                      onClick={() => fetchOrderDetails(order)}
                      className="px-3 py-1 text-xs bg-primary/10 text-primary rounded-md hover:bg-primary/10 font-medium"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Main Gift Cards View
    return (
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="mb-7 flex flex-wrap items-end justify-between gap-4 border-b border-border pb-5">
              <div><p className="text-sm text-muted-foreground">Buy and redeem gift cards</p><h1 className="mt-1 text-3xl font-extrabold leading-tight tracking-tight lg:text-[38px]">Gift cards</h1></div>
            </div>
          </div>
          <button
            onClick={() => { setShowHistory(true); fetchOrderHistory(); }}
            className="px-3 py-2 text-sm bg-muted text-muted-foreground rounded-md hover:bg-muted"
          >
            Order History
          </button>
        </div>
        
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search gift cards..."
              className="w-full px-4 py-2 border rounded-md text-sm"
            />
          </div>
          {/* Searchable Country Dropdown */}
          <div className="relative">
            <input
              type="text"
              value={countrySearch}
              onChange={(e) => { setCountrySearch(e.target.value); setShowCountryDropdown(true); }}
              onFocus={() => setShowCountryDropdown(true)}
              placeholder="Select Country..."
              className="w-full sm:w-56 px-4 py-2 border rounded-md text-sm bg-card"
            />
            {selectedCountry && (
              <button
                onClick={() => { setSelectedCountry(''); setCountrySearch(''); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            {showCountryDropdown && (
              <div className="absolute z-50 mt-1 w-full bg-card border rounded-md shadow-lg max-h-60 overflow-y-auto">
                <button
                  onClick={() => { setSelectedCountry(''); setCountrySearch(''); setShowCountryDropdown(false); }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-muted"
                >
                  All Countries
                </button>
                {countries
                  .filter(c => c.name?.toLowerCase().includes(countrySearch.toLowerCase()))
                  .slice(0, 50)
                  .map((c) => (
                    <button
                      key={c.isoName}
                      onClick={() => { 
                        setSelectedCountry(c.isoName); 
                        setCountrySearch(c.name); 
                        setShowCountryDropdown(false); 
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-muted"
                    >
                      {c.name}
                    </button>
                  ))
                }
              </div>
            )}
          </div>
        </div>
        
        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 mx-auto text-muted-foreground animate-spin" />
            <p className="text-muted-foreground mt-2">Loading gift cards...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12 bg-card rounded-lg border">
            <Gift className="w-12 h-12 mx-auto text-muted-foreground" />
            <p className="text-muted-foreground mt-2">No gift cards found</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {filteredProducts.slice(0, 50).map((product) => (
              <div
                key={product.productId}
                onClick={() => setSelectedProduct(product)}
                className="bg-card rounded-lg border p-3 sm:p-4 cursor-pointer hover:shadow-lg hover:-translate-y-1 transition-all"
              >
                {product.logoUrls?.[0] ? (
                  <img 
                    src={product.logoUrls[0]} 
                    alt="" 
                    className="w-full h-16 sm:h-20 object-contain mb-2 bg-muted/50 rounded-md"
                  />
                ) : (
                  <div className="w-full h-16 sm:h-20 bg-muted rounded-md flex items-center justify-center mb-2">
                    <Gift className="w-8 h-8 text-muted-foreground" />
                  </div>
                )}
                <h3 className="font-semibold text-foreground text-xs sm:text-sm truncate">{product.brand?.brandName}</h3>
                <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{product.country?.name}</p>
                <div className="mt-2 text-xs text-primary font-medium">
                  {product.fixedRecipientDenominations?.length > 0 
                    ? `${product.recipientCurrencyCode} ${product.fixedRecipientDenominations[0]}+`
                    : `${product.recipientCurrencyCode} ${product.minRecipientDenomination}-${product.maxRecipientDenomination}`
                  }
                </div>
              </div>
            ))}
          </div>
        )}
        
        {filteredProducts.length > 50 && (
          <p className="text-center text-sm text-muted-foreground">
            Showing 50 of {filteredProducts.length} products. Use search to find specific cards.
          </p>
        )}
      </div>
    );
  }


  // Login Popup Modal Component
  const LoginPopupModal = () => {
    if (!showLoginPopup || loginPopups.length === 0) return null;
    
    const popup = loginPopups[currentPopupIndex];
    if (!popup) return null;

    const getPopupIcon = () => {
      switch (popup.popup_type) {
        case 'promo':
          return <Gift className="w-12 h-12 text-primary" />;
        case 'support':
          return <MessageSquare className="w-12 h-12 text-primary" />;
        case 'deposit_bonus':
          return <Wallet className="w-12 h-12 text-primary" />;
        case 'downtime':
          return <Bell className="w-12 h-12 text-destructive" />;
        default:
          return <Bell className="w-12 h-12 text-muted-foreground" />;
      }
    };

    const getPopupColor = () => {
      switch (popup.popup_type) {
        case 'promo':
          return 'from-green-500 to-emerald-600';
        case 'support':
          return 'from-primary to-primary';
        case 'deposit_bonus':
          return 'from-primary to-primary';
        case 'downtime':
          return 'from-red-500 to-orange-600';
        default:
          return 'from-gray-500 to-gray-600';
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
        <div className="bg-card rounded-lg shadow-2xl max-w-md w-full overflow-hidden">
          {/* Header with gradient */}
          <div className={`bg-gradient-to-r ${getPopupColor()} p-6 text-white text-center`}>
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
              {getPopupIcon()}
            </div>
            <h2 className="text-xl font-bold">{popup.title}</h2>
          </div>
          
          {/* Content */}
          <div className="p-6">
            {popup.image_url && (
              <img 
                src={popup.image_url} 
                alt="" 
                className="w-full h-40 object-cover rounded-md mb-4"
              />
            )}
            <p className="text-muted-foreground text-center whitespace-pre-wrap">{popup.message}</p>
            
            {/* Action Buttons */}
            <div className="mt-6 space-y-3">
              {popup.action_url && (
                <a
                  href={popup.action_url}
                  target={popup.action_url.startsWith('http') ? '_blank' : '_self'}
                  rel="noopener noreferrer"
                  className={`block w-full py-3 bg-gradient-to-r ${getPopupColor()} text-white rounded-lg font-semibold text-center hover:opacity-90 transition-opacity`}
                >
                  {popup.action_text || 'Learn More'}
                </a>
              )}
              <button
                onClick={dismissLoginPopup}
                className="w-full py-3 border border-border text-muted-foreground rounded-lg font-medium hover:bg-muted/50 transition-colors"
              >
                {currentPopupIndex < loginPopups.length - 1 ? 'Next' : 'Close'}
              </button>
            </div>
            
            {/* Popup counter */}
            {loginPopups.length > 1 && (
              <p className="text-center text-xs text-muted-foreground mt-3">
                {currentPopupIndex + 1} of {loginPopups.length}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <AutoLogout timeoutMinutes={15} warningMinutes={2}>
      <LoginPopupModal />
      {renderDashboard()}
    </AutoLogout>
  );
};

export default NewDashboard;
